import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  ChevronDown,
  Clock3,
  CreditCard,
  FileText,
  Fingerprint,
  Gift,
  MapPin,
  MessageSquareText,
  Printer,
  ScanSearch,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Star,
  Users,
} from "lucide-react";
import { track } from "@amplitude/analytics-browser";
import CustomCaseFormFields, {
  type CustomCaseFormCopy,
} from "../components/custom/CustomCaseFormFields";
import { CustomOrderAPI, storeCustomOrderOwnerSessionToken } from "../lib/api";
import { cartService } from "../services/cartService";
import {
  clearPendingCustomOrderAccessToken,
  clearStoredCustomCaseFormData,
  clearLastCustomOrderAccessToken,
  CUSTOM_CASE_COMING_SOON_ENABLED,
  CUSTOM_CASE_PRODUCT_PATH,
  CustomCaseFormData,
  CustomCaseFormErrors,
  clearLastCustomOrderEmail,
  getEmptySuspect,
  getLastCustomOrderAccessToken,
  getLastCustomOrderEmail,
  getPendingCustomOrderAccessToken,
  loadStoredCustomCaseFormData,
  savePendingCustomOrderAccessToken,
  saveLastCustomOrderEmail,
  storeCustomCaseFormData,
  validateCustomCaseForm,
} from "../utils/customCase";
import {
  MARKETING_BODY_READING_CLASS,
  MARKETING_CARD_BODY_CLASS,
  MARKETING_CARD_TITLE_CLASS,
  MARKETING_CTA_TEXT_CLASS,
  MARKETING_HERO_TITLE_CLASS,
  MARKETING_SECTION_TITLE_CLASS,
} from "../utils/caseViewTypography";
import {
  isFastSpringPopupPurchaseComplete,
  launchCustomCheckout,
  type FastSpringPopupClosePayload,
} from "../utils/customCheckout";
import {
  buildCustomLandingPath,
  getCustomLandingTopicLabel,
  normalizeCustomLandingContext,
} from "../utils/customLanding";
import { getLocalizedCustomCaseApiMessage } from "../utils/customCaseApiErrors";
import { devError, devLog, devWarn } from "../utils/devLog";

const TRUST_BADGES = [
  { icon: ShieldCheck, label: "Secure checkout" },
  { icon: Clock3, label: "Instant delivery" },
  { icon: Users, label: "Built for 2-6 players" },
  { icon: Smartphone, label: "Digital PDF" },
];

const HOW_IT_WORKS = [
  {
    icon: CreditCard,
    title: "Buy the case",
    text: "Simple checkout. No lengthy forms before payment.",
  },
  {
    icon: MessageSquareText,
    title: "Fill the brief",
    text: "Add city, names, relationships, and choose the story.",
  },
  {
    icon: ScanSearch,
    title: "We build it",
    text: "Your details become clues, motives, and red herrings.",
  },
  {
    icon: Printer,
    title: "Solve it",
    text: "Receive your case file to read on screen or print.",
  },
];

const DELIVERABLES = [
  {
    icon: Fingerprint,
    title: "Suspect dossiers",
    text: "Detailed profiles built around your chosen people, their relationships, and jobs.",
  },
  {
    icon: FileText,
    title: "Evidence packet",
    text: "Clues, red herrings, and case notes arranged like a private investigative file.",
  },
  {
    icon: Sparkles,
    title: "Motive web",
    text: "A story structure that turns existing relationships into believable suspicion.",
  },
  {
    icon: BadgeCheck,
    title: "Final reveal",
    text: "A clear solution so the game has a satisfying ending, you must request the arrest warrant to unlock the solution.",
  },
];

const MOMENT_USE_CASES = [
  {
    title: "Date night",
    text: "Make the two of you the center of a mystery that feels more personal than dinner and a movie.",
  },
  {
    title: "Birthday surprise",
    text: "Give them a case file where their friends become suspects, witnesses, and possible liars.",
  },
  {
    title: "Friend group game night",
    text: "Use the built in case sharing functionality to invite friends to play the case with you.",
  },
  {
    title: "Anniversary or gift reveal",
    text: "Hand over something made for the recipient instead of another generic downloadable game.",
  },
];

const DELIVERY_STEPS = [
  "Make a secure payment.",
  "Enter your city and suspects.",
  "Pick the story you like best.",
  "Generate the case.",
];

const FAQS = [
  {
    question: "Do I need all the details before I buy?",
    answer:
      "No. You can complete the custom case intake later. Once you buy the case you can come back later to complete the city, suspects, and pick the story you like best.",
  },
  {
    question: "What do I actually receive?",
    answer:
      "A structured digital case file with story background, suspect dossiers, evidence, emails, texts, autopsy, red herrings, and the final solution. It can be viewed on your phone, tablet, desktop, or printed out.",
  },
  {
    question: "Can I come back later to complete the intake?",
    answer: "Yes. The custom case intake is resumable from this browser",
  },
  {
    question: "Is this different from a premade mystery?",
    answer:
      "Yes. Premade cases are ready-made stories. This one uses your chosen city and your selected people as the suspects in the story. You then choose from 3 different storylines to fit your group's interests.",
  },
  {
    question: "How long does it take to receive my case?",
    answer:
      "Checkout is immediate. Once you complete the custom case intake information, the build starts when you submit it. Your custom case gets built while you wait. The finished case is delivered through your private access link in about 5 minutes.",
  },
  {
    question: "Can I print it or do I have to read on screen?",
    answer:
      "Both. The case is optimized for viewing on your phone, tablet, or desktop, but it also can be printed out.",
  },
  {
    question: "Can I share the case with friends?",
    answer:
      "Yes, you can share the case with friends. You can setup a guest link to share the case with friends. However, you are not permitted to sell the cases to others or charge for them.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "My friends couldn't believe they were the suspects. We spent hours accusing each other. Best birthday gift ever.",
    name: "Sarah M.",
    location: "Austin, TX",
    tag: "Birthday Party",
  },
  {
    quote:
      "So much better than another escape room. Having our actual relationship as the backdrop made it hilarious and personal.",
    name: "Jake & Emma",
    location: "Portland, OR",
    tag: "Date Night",
  },
  {
    quote:
      "It was so easy to enter the details and then we had a blast playing the game.",
    name: "Marcus T.",
    location: "Chicago, IL",
    tag: "Game Night",
  },
];

const SUMMARY_STATS = [
  { value: "", label: "Cases created this month" },
  { value: "4.9", label: "Average rating" },
  { value: "5mins", label: "Average build time" },
  { value: "100%", label: "Satisfaction guarantee" },
];

const CASES_THIS_MONTH_BASELINE = {
  year: 2026,
  monthIndex: 4,
  count: 247,
};

const getCasesThisMonthCount = (date: Date) => {
  const monthOffset =
    (date.getFullYear() - CASES_THIS_MONTH_BASELINE.year) * 12 +
    (date.getMonth() - CASES_THIS_MONTH_BASELINE.monthIndex);

  if (monthOffset <= 0) {
    return CASES_THIS_MONTH_BASELINE.count;
  }

  let total = CASES_THIS_MONTH_BASELINE.count;
  for (let index = 0; index < monthOffset; index += 1) {
    total += index % 2 === 0 ? 4 : 3;
  }

  return total;
};

const localizeFormErrors = (
  errors: CustomCaseFormErrors,
  lang: keyof typeof CUSTOM_ENTRY_COPY,
): CustomCaseFormErrors => {
  if (lang === "en") return errors;

  const messageMap: Record<string, Record<string, string>> = {
    fr: {
      "Please enter a valid email": "Veuillez entrer un email valide",
      "Enter your email before starting checkout.":
        "Entrez votre email avant de lancer le paiement.",
      "Review and accept the privacy and order-access notice before starting checkout.":
        "Veuillez accepter l'avis de confidentialite et d'acces avant de lancer le paiement.",
      "Your first name is required": "Votre prenom est requis",
      "Email is required": "L'email est requis",
      "City is required": "La ville est requise",
      "Please tell us where you heard about us":
        "Dites-nous ou vous avez entendu parler de nous",
      "First name is required": "Le prenom est requis",
      "Age is required": "L'age est requis",
      "Sex is required": "Le sexe est requis",
      "Hair color is required": "La couleur des cheveux est requise",
      "Skin tone is required": "La teinte de peau est requise",
      "Occupation is required": "La profession est requise",
      "Please add at least 1 suspects": "Veuillez ajouter au moins 1 suspect",
      "Suspects should use first names only for custom cases":
        "Utilisez uniquement des prenoms pour les suspects des affaires personnalisees",
    },
    es: {
      "Please enter a valid email": "Introduce un correo valido",
      "Enter your email before starting checkout.":
        "Introduce tu correo antes de iniciar el pago.",
      "Review and accept the privacy and order-access notice before starting checkout.":
        "Revisa y acepta el aviso de privacidad y acceso antes de iniciar el pago.",
      "Your first name is required": "Tu nombre es obligatorio",
      "Email is required": "El correo es obligatorio",
      "City is required": "La ciudad es obligatoria",
      "Please tell us where you heard about us": "Dinos donde nos conociste",
      "First name is required": "El nombre es obligatorio",
      "Age is required": "La edad es obligatoria",
      "Sex is required": "El sexo es obligatorio",
      "Hair color is required": "El color de pelo es obligatorio",
      "Skin tone is required": "El tono de piel es obligatorio",
      "Occupation is required": "La ocupacion es obligatoria",
      "Please add at least 1 suspects": "Anade al menos 1 sospechoso",
      "Suspects should use first names only for custom cases":
        "Usa solo nombres de pila para los sospechosos del caso personalizado",
    },
    de: {
      "Please enter a valid email": "Bitte gib eine gultige E-Mail ein",
      "Enter your email before starting checkout.":
        "Gib deine E-Mail ein, bevor du den Checkout startest.",
      "Review and accept the privacy and order-access notice before starting checkout.":
        "Bitte akzeptiere den Hinweis zu Datenschutz und Bestellzugang, bevor du den Checkout startest.",
      "Your first name is required": "Dein Vorname ist erforderlich",
      "Email is required": "E-Mail ist erforderlich",
      "City is required": "Die Stadt ist erforderlich",
      "Please tell us where you heard about us":
        "Bitte sag uns, wo du von uns erfahren hast",
      "First name is required": "Der Vorname ist erforderlich",
      "Age is required": "Das Alter ist erforderlich",
      "Sex is required": "Das Geschlecht ist erforderlich",
      "Hair color is required": "Die Haarfarbe ist erforderlich",
      "Skin tone is required": "Der Hautton ist erforderlich",
      "Occupation is required": "Der Beruf ist erforderlich",
      "Please add at least 1 suspects":
        "Bitte fuge mindestens 1 verdachtige Person hinzu",
      "Suspects should use first names only for custom cases":
        "Verwende fur individuelle Falle nur Vornamen bei Verdachtigen",
    },
    it: {
      "Please enter a valid email": "Inserisci un'email valida",
      "Enter your email before starting checkout.":
        "Inserisci la tua email prima di avviare il pagamento.",
      "Review and accept the privacy and order-access notice before starting checkout.":
        "Accetta l'avviso sulla privacy e sull'accesso all'ordine prima di avviare il pagamento.",
      "Your first name is required": "Il tuo nome e obbligatorio",
      "Email is required": "L'email e obbligatoria",
      "City is required": "La citta e obbligatoria",
      "Please tell us where you heard about us": "Dicci dove ci hai conosciuti",
      "First name is required": "Il nome e obbligatorio",
      "Age is required": "L'eta e obbligatoria",
      "Sex is required": "Il sesso e obbligatorio",
      "Hair color is required": "Il colore dei capelli e obbligatorio",
      "Skin tone is required": "Il tono della pelle e obbligatorio",
      "Occupation is required": "La professione e obbligatoria",
      "Please add at least 1 suspects": "Aggiungi almeno 1 sospetto",
      "Suspects should use first names only for custom cases":
        "Usa solo i nomi di battesimo per i sospetti del caso personalizzato",
    },
  };

  const translate = (message?: string) =>
    message ? messageMap[lang]?.[message] || message : message;

  return {
    ...errors,
    firstName: translate(errors.firstName),
    email: translate(errors.email),
    city: translate(errors.city),
    location: translate(errors.location),
    source: translate(errors.source),
    suspects: Array.isArray(errors.suspects)
      ? errors.suspects.map((error) => ({
          ...error,
          message: translate(error.message) || error.message,
        }))
      : errors.suspects,
  };
};

const CUSTOM_ENTRY_COPY = {
  en: {
    eyebrow: "Signature custom cold case",
    heroTitle: "Turn your friends into suspects.",
    heroDescription:
      "Create a custom murder mystery where your friends are the suspects. You pick the city, the names, and the relationships. We build the case file.",
    comingSoonEyebrow: "Coming soon",
    comingSoonTitle: "Custom cold cases are coming soon.",
    comingSoonDescription:
      "We are building a custom mystery experience where your group becomes the suspect list. Join the notification list and we will let you know when custom cases are ready.",
    startCaseLabel: "Start your case",
    emailLabel: "Email to receive your case file",
    policyNotice:
      "I agree to the Terms and Privacy Policy, and I understand you will email me about order access, delivery, and support for this purchase.",
    marketingOptIn:
      "Email me promotions, new case drops, and special offers. I can unsubscribe any time.",
    startButton: "Start My Custom Case",
    preparingButton: "Preparing Checkout...",
    payFirstNote: "Pay first, then fill in the details. Takes only 5 minutes.",
    resumeTitle: "We found your in-progress case",
    resumeBody: "Pick up where you left off instead of starting a new order.",
    continueResume: "Continue Previous Intake",
    startFresh: "Start Fresh Instead",
    checkoutStatusLabel: "Checkout Status",
    plannerEyebrow: "Optional Planner",
    plannerTitle: "Want to sketch the case before you buy?",
    plannerBody:
      "This is optional. The fastest path is still to buy now and finish the full brief after checkout. If you want to capture ideas first, we will save them into the order before checkout opens.",
    openPlanner: "Open Planner",
    hidePlanner: "Hide Planner",
    draftReady: "Planner data is already saved in this browser.",
    draftEmpty: "No planner draft started yet.",
    cityEmpty: "Not chosen yet",
    finalEyebrow: "Ready When You Are",
    finalTitle: "Start the custom case now.",
    finalSubtitle: "Fill in the details after checkout.",
    finalBody: "Our simple custom case intake takes only 5 minutes.",
    finalButton: "Start My Custom Case",
  },
  fr: {
    eyebrow: "Affaire personnalisee signature",
    heroTitle: "Transformez vos proches en suspects.",
    heroDescription:
      "Creez un mystere personnalise ou vos proches deviennent les suspects. Vous choisissez la ville, les noms et les relations. Nous construisons le dossier.",
    comingSoonEyebrow: "Bientot disponible",
    comingSoonTitle: "Les affaires personnalisees arrivent bientot.",
    comingSoonDescription:
      "Nous preparons une experience de mystere personnalisee ou votre groupe devient la liste des suspects.",
    startCaseLabel: "Commencez votre affaire",
    emailLabel: "Email pour recevoir votre dossier",
    policyNotice:
      "J'accepte les Conditions et la Politique de confidentialite, et je comprends que vous m'ecrirez au sujet de l'acces, de la livraison et du support.",
    marketingOptIn:
      "Envoyez-moi les promotions, les nouvelles affaires et les offres speciales. Je peux me desinscrire a tout moment.",
    startButton: "Commencer mon affaire personnalisee",
    preparingButton: "Preparation du paiement...",
    payFirstNote:
      "Payez d'abord, puis completez les details. Cela prend environ 5 minutes.",
    resumeTitle: "Nous avons retrouve votre affaire en cours",
    resumeBody: "Reprenez la ou vous vous etiez arrete au lieu de recommencer.",
    continueResume: "Reprendre l'affaire",
    startFresh: "Recommencer",
    checkoutStatusLabel: "Statut du paiement",
    plannerEyebrow: "Planificateur optionnel",
    plannerTitle: "Vous voulez esquisser l'affaire avant d'acheter ?",
    plannerBody:
      "C'est optionnel. Le chemin le plus rapide reste d'acheter maintenant puis de finir le brief prive apres le paiement.",
    openPlanner: "Ouvrir le planificateur",
    hidePlanner: "Masquer le planificateur",
    draftReady: "Un brouillon est deja enregistre dans ce navigateur.",
    draftEmpty: "Aucun brouillon n'a encore ete commence.",
    cityEmpty: "Ville non choisie",
    finalEyebrow: "Quand vous etes pret",
    finalTitle: "Commencez l'affaire personnalisee maintenant.",
    finalSubtitle: "Ajoutez les details apres le paiement.",
    finalBody: "Le brief personnalise prend environ 5 minutes.",
    finalButton: "Commencer mon affaire",
  },
  es: {
    eyebrow: "Caso personalizado premium",
    heroTitle: "Convierte a tus amigos en sospechosos.",
    heroDescription:
      "Crea un misterio personalizado donde tus amigos son los sospechosos. Tu eliges la ciudad, los nombres y las relaciones. Nosotros construimos el expediente.",
    comingSoonEyebrow: "Proximamente",
    comingSoonTitle: "Los casos personalizados llegan pronto.",
    comingSoonDescription:
      "Estamos preparando una experiencia personalizada donde tu grupo se convierte en la lista de sospechosos.",
    startCaseLabel: "Empieza tu caso",
    emailLabel: "Correo para recibir tu expediente",
    policyNotice:
      "Acepto los Terminos y la Politica de Privacidad, y entiendo que me escribiran sobre acceso, entrega y soporte del pedido.",
    marketingOptIn:
      "Quiero recibir promociones, nuevos casos y ofertas especiales. Puedo cancelar cuando quiera.",
    startButton: "Empezar mi caso personalizado",
    preparingButton: "Preparando el pago...",
    payFirstNote:
      "Primero pagas y luego completas los detalles. Tarda unos 5 minutos.",
    resumeTitle: "Encontramos tu caso en progreso",
    resumeBody: "Retoma donde lo dejaste en lugar de empezar otro pedido.",
    continueResume: "Continuar mi formulario",
    startFresh: "Empezar de nuevo",
    checkoutStatusLabel: "Estado del pago",
    plannerEyebrow: "Planificador opcional",
    plannerTitle: "Quieres bosquejar el caso antes de comprar?",
    plannerBody:
      "Es opcional. La via mas rapida sigue siendo pagar ahora y completar el formulario privado despues.",
    openPlanner: "Abrir planificador",
    hidePlanner: "Ocultar planificador",
    draftReady: "Ya hay un borrador guardado en este navegador.",
    draftEmpty: "Todavia no hay borrador guardado.",
    cityEmpty: "Ciudad sin elegir",
    finalEyebrow: "Cuando quieras",
    finalTitle: "Empieza el caso personalizado ahora.",
    finalSubtitle: "Completa los detalles despues del pago.",
    finalBody: "El formulario personalizado tarda unos 5 minutos.",
    finalButton: "Empezar mi caso",
  },
  de: {
    eyebrow: "Individueller Premium-Fall",
    heroTitle: "Mach deine Freunde zu Verdachtigen.",
    heroDescription:
      "Erstelle einen personlichen Kriminalfall, in dem deine Freunde die Verdachtigen sind. Du bestimmst Stadt, Namen und Beziehungen. Wir bauen die Fallakte.",
    comingSoonEyebrow: "Demnachst",
    comingSoonTitle: "Individuelle Kriminalfalle kommen bald.",
    comingSoonDescription:
      "Wir bauen gerade ein individuelles Erlebnis, bei dem eure Gruppe zur Verdachtigenliste wird.",
    startCaseLabel: "Starte deinen Fall",
    emailLabel: "E-Mail fur deine Fallakte",
    policyNotice:
      "Ich akzeptiere die Nutzungsbedingungen und die Datenschutzerklarung und verstehe, dass ihr mir zu Zugriff, Lieferung und Support schreiben durft.",
    marketingOptIn:
      "Sendet mir Angebote, neue Falle und Sonderaktionen. Ich kann mich jederzeit abmelden.",
    startButton: "Meinen individuellen Fall starten",
    preparingButton: "Checkout wird vorbereitet...",
    payFirstNote:
      "Zuerst bezahlen, dann die Details ausfullen. Das dauert nur etwa 5 Minuten.",
    resumeTitle: "Wir haben deinen begonnenen Fall gefunden",
    resumeBody: "Mach dort weiter, wo du aufgehort hast, statt neu zu starten.",
    continueResume: "Vorherigen Intake fortsetzen",
    startFresh: "Neu starten",
    checkoutStatusLabel: "Checkout-Status",
    plannerEyebrow: "Optionaler Planer",
    plannerTitle: "Willst du den Fall vor dem Kauf skizzieren?",
    plannerBody:
      "Das ist optional. Der schnellste Weg bleibt: jetzt kaufen und den privaten Intake danach abschliessen.",
    openPlanner: "Planer offnen",
    hidePlanner: "Planer ausblenden",
    draftReady: "Planer-Daten sind bereits in diesem Browser gespeichert.",
    draftEmpty: "Noch kein Planer-Entwurf gespeichert.",
    cityEmpty: "Noch keine Stadt gewahlt",
    finalEyebrow: "Wenn du bereit bist",
    finalTitle: "Starte den individuellen Fall jetzt.",
    finalSubtitle: "Die Details kommen nach dem Checkout.",
    finalBody: "Der individuelle Intake dauert nur etwa 5 Minuten.",
    finalButton: "Meinen Fall starten",
  },
  it: {
    eyebrow: "Caso personalizzato premium",
    heroTitle: "Trasforma i tuoi amici in sospetti.",
    heroDescription:
      "Crea un mistero personalizzato in cui i tuoi amici sono i sospetti. Tu scegli citta, nomi e relazioni. Noi costruiamo il fascicolo.",
    comingSoonEyebrow: "In arrivo",
    comingSoonTitle: "I casi personalizzati stanno arrivando.",
    comingSoonDescription:
      "Stiamo preparando un'esperienza personalizzata in cui il tuo gruppo diventa la lista dei sospetti.",
    startCaseLabel: "Inizia il tuo caso",
    emailLabel: "Email per ricevere il fascicolo",
    policyNotice:
      "Accetto i Termini e la Privacy Policy e capisco che mi scriverete per accesso, consegna e supporto relativo all'ordine.",
    marketingOptIn:
      "Inviatemi promozioni, nuovi casi e offerte speciali. Posso disiscrivermi in qualsiasi momento.",
    startButton: "Inizia il mio caso personalizzato",
    preparingButton: "Preparazione del pagamento...",
    payFirstNote:
      "Prima paghi, poi completi i dettagli. Richiede circa 5 minuti.",
    resumeTitle: "Abbiamo trovato il tuo caso in corso",
    resumeBody: "Riprendi da dove avevi lasciato invece di iniziare da zero.",
    continueResume: "Continua il mio modulo",
    startFresh: "Ricominciamo",
    checkoutStatusLabel: "Stato del pagamento",
    plannerEyebrow: "Planner opzionale",
    plannerTitle: "Vuoi abbozzare il caso prima di acquistare?",
    plannerBody:
      "E facoltativo. Il percorso piu rapido resta acquistare ora e completare il brief privato dopo il checkout.",
    openPlanner: "Apri planner",
    hidePlanner: "Nascondi planner",
    draftReady: "C'e gia una bozza salvata in questo browser.",
    draftEmpty: "Nessuna bozza salvata per ora.",
    cityEmpty: "Citta non ancora scelta",
    finalEyebrow: "Quando vuoi",
    finalTitle: "Inizia ora il caso personalizzato.",
    finalSubtitle: "Aggiungi i dettagli dopo il checkout.",
    finalBody: "Il brief personalizzato richiede circa 5 minuti.",
    finalButton: "Inizia il mio caso",
  },
} as const;

const buildLocalizedPageContent = (lang: keyof typeof CUSTOM_ENTRY_COPY) => {
  if (lang === "fr") {
    return {
      trustBadges: [
        "Paiement securise",
        "Livraison instantanee",
        "Concu pour 2 a 6 joueurs",
        "PDF numerique",
      ],
      panelBenefits: [
        "Dossier personnalise avec votre ville",
        "Profils de suspects avec vos noms",
        "Indices et fausses pistes sur mesure",
        "Imprimable ou jouable a l'ecran",
        "Solution finale incluse",
      ],
      oneTime: "Paiement unique",
      noSubscription: "Sans abonnement",
      panelHowItWorks: "Comment ca marche",
      deliverySteps: [
        "Effectuez un paiement securise.",
        "Entrez votre ville et vos suspects.",
        "Choisissez l'histoire que vous preferez.",
        "Generez l'affaire.",
      ],
      howItWorksEyebrow: "Comment ca marche",
      howItWorksTitle: "Du paiement au dossier final en quelques minutes",
      howItWorks: [
        {
          title: "Achetez l'affaire",
          text: "Paiement simple. Aucun long formulaire avant l'achat.",
        },
        {
          title: "Remplissez le brief",
          text: "Ajoutez la ville, les noms, les relations et choisissez l'intrigue.",
        },
        {
          title: "Nous la construisons",
          text: "Vos details deviennent indices, mobiles et fausses pistes.",
        },
        {
          title: "Resolvez-la",
          text: "Recevez votre dossier prive a lire a l'ecran ou a imprimer.",
        },
      ],
      deliverablesEyebrow: "Ce que vous recevez",
      deliverablesTitle: "Chaque affaire est construite pour vous.",
      deliverablesBody:
        "Notre AI Wizard prend votre ville, vos suspects et l'histoire choisie puis construit une enquete complete pendant que vous attendez.",
      deliverablesCta: "Demarrer l'affaire",
      deliverables: [
        {
          title: "Profils de suspects",
          text: "Des fiches detaillees basees sur vos personnes, leurs relations et leurs metiers.",
        },
        {
          title: "Paquet de preuves",
          text: "Indices, fausses pistes et notes d'enquete presentes comme un vrai dossier.",
        },
        {
          title: "Reseau de mobiles",
          text: "Une structure d'histoire qui transforme vos relations en soupcons credibles.",
        },
        {
          title: "Revelation finale",
          text: "Une solution claire pour une conclusion satisfaisante, debloquee apres le mandat d'arrestation.",
        },
      ],
      whyEyebrow: "Pourquoi le sur-mesure est meilleur",
      whyTitle: "Vous connaissez deja les suspects.",
      whyBody:
        "Une affaire prefaite vous demande de vous interesser a des inconnus. Ici, l'histoire est construite autour de personnes que vous connaissez deja.",
      moments: [
        {
          title: "Soiree en amoureux",
          text: "Faites de votre duo le centre d'un mystere plus marquant qu'un diner cinema.",
        },
        {
          title: "Surprise d'anniversaire",
          text: "Offrez un dossier ou les amis deviennent suspects, temoins et possibles menteurs.",
        },
        {
          title: "Soiree jeux entre amis",
          text: "Utilisez le partage integre pour inviter vos proches a enqueter avec vous.",
        },
        {
          title: "Cadeau ou anniversaire de couple",
          text: "Offrez quelque chose de vraiment concu pour la personne plutot qu'un jeu generique.",
        },
      ],
      citySpecificTitle: "Atmosphere liee a votre ville",
      citySpecificBody: "Le decor semble familier plutot qu'anonyme.",
      relationshipTitle: "Indices fondes sur les relations",
      relationshipBody:
        "Les personnes du dossier ont deja une histoire entre elles.",
      detectivesEyebrow: "Vrais detectives",
      detectivesTitle: "Affaires fermees. Suspects accuses.",
      testimonials: [
        {
          quote:
            "Mes amis n'en revenaient pas d'etre les suspects. On s'est accuses pendant des heures. Le meilleur cadeau d'anniversaire.",
          name: "Sarah M.",
          location: "Austin, TX",
          tag: "Anniversaire",
        },
        {
          quote:
            "Bien mieux qu'une autre escape room. Notre vraie relation dans l'histoire rendait le tout hilarant et personnel.",
          name: "Jake & Emma",
          location: "Portland, OR",
          tag: "Soiree en amoureux",
        },
        {
          quote:
            "C'etait tres simple de saisir les details et on a passe un moment genial a jouer l'affaire.",
          name: "Marcus T.",
          location: "Chicago, IL",
          tag: "Soiree jeux",
        },
      ],
      summaryLabels: [
        "Affaires creees ce mois-ci",
        "Note moyenne",
        "Temps moyen de creation",
        "Garantie satisfaction",
      ],
      faqEyebrow: "Questions repondues",
      faqTitle: "Avant d'acheter",
      faqBody:
        "Des reponses rapides pour lever les hesitations avant l'achat d'une affaire personnalisee.",
      faqs: [
        {
          question: "Dois-je avoir tous les details avant d'acheter ?",
          answer:
            "Non. Vous pouvez terminer l'intake plus tard. Une fois l'affaire achetee, vous pouvez revenir pour completer la ville, les suspects et choisir l'histoire qui vous convient.",
        },
        {
          question: "Que vais-je recevoir exactement ?",
          answer:
            "Un dossier numerique structure avec contexte, profils de suspects, preuves, emails, textos, autopsie, fausses pistes et solution finale. Il peut etre lu sur telephone, tablette, ordinateur ou imprime.",
        },
        {
          question: "Puis-je revenir plus tard pour terminer l'intake ?",
          answer:
            "Oui. L'intake personnalise est reprenable depuis ce navigateur.",
        },
        {
          question: "Est-ce different d'une affaire deja prete ?",
          answer:
            "Oui. Les affaires pretes sont fixes. Ici, votre ville et vos personnes deviennent les suspects de l'histoire, puis vous choisissez parmi 3 directions narratives.",
        },
        {
          question: "Combien de temps faut-il pour recevoir mon affaire ?",
          answer:
            "Le paiement est immediat. Une fois l'intake complete, la construction commence et votre affaire finale est livree sur votre lien prive en environ 5 minutes.",
        },
        {
          question: "Puis-je l'imprimer ou faut-il jouer en ligne ?",
          answer:
            "Les deux. L'affaire est optimisee pour ecran mais peut aussi etre imprimee.",
        },
        {
          question: "Puis-je partager l'affaire avec des amis ?",
          answer:
            "Oui, via un lien invite. En revanche, vous ne pouvez pas revendre l'affaire ni faire payer l'acces.",
        },
      ],
      plannerStatusLabel: "Statut du brouillon",
      suspectsLabel: "Suspects",
      addUpTo: "Ajoutez jusqu'a 6 suspects",
      cityLabel: "Ville",
      secureViaFastSpring: "Paiement securise via FastSpring",
      instantDelivery: "Livraison instantanee",
      buildersSuffix: "personnes creent une affaire en ce moment",
    };
  }

  if (lang === "es") {
    return {
      trustBadges: [
        "Pago seguro",
        "Entrega instantanea",
        "Pensado para 2 a 6 jugadores",
        "PDF digital",
      ],
      panelBenefits: [
        "Expediente personalizado con tu ciudad",
        "Perfiles de sospechosos con tus nombres",
        "Pistas y falsas pistas a medida",
        "Imprimible o jugable en pantalla",
        "Solucion final incluida",
      ],
      oneTime: "Pago unico",
      noSubscription: "Sin suscripcion",
      panelHowItWorks: "Como funciona",
      deliverySteps: [
        "Haz un pago seguro.",
        "Introduce tu ciudad y tus sospechosos.",
        "Elige la historia que mas te guste.",
        "Genera el caso.",
      ],
      howItWorksEyebrow: "Como funciona",
      howItWorksTitle: "Del pago al expediente final en minutos",
      howItWorks: [
        {
          title: "Compra el caso",
          text: "Pago sencillo. Sin formularios largos antes de comprar.",
        },
        {
          title: "Rellena el brief",
          text: "Anade ciudad, nombres, relaciones y elige la trama.",
        },
        {
          title: "Lo construimos",
          text: "Tus datos se convierten en pistas, moviles y falsas pistas.",
        },
        {
          title: "Resuelvelo",
          text: "Recibe tu expediente privado para leerlo en pantalla o imprimirlo.",
        },
      ],
      deliverablesEyebrow: "Lo que recibes",
      deliverablesTitle: "Cada caso se construye para ti.",
      deliverablesBody:
        "Nuestro AI Wizard toma tu ciudad, tus sospechosos y la historia elegida para construir un misterio completo mientras esperas.",
      deliverablesCta: "Empezar el caso",
      deliverables: [
        {
          title: "Perfiles de sospechosos",
          text: "Fichas detalladas basadas en las personas que elijas, sus relaciones y trabajos.",
        },
        {
          title: "Paquete de pruebas",
          text: "Pistas, falsas pistas y notas de investigacion ordenadas como un expediente privado.",
        },
        {
          title: "Red de moviles",
          text: "Una estructura narrativa que convierte relaciones reales en sospechas creibles.",
        },
        {
          title: "Revelacion final",
          text: "Una solucion clara para un final satisfactorio, desbloqueada al pedir la orden de arresto.",
        },
      ],
      whyEyebrow: "Por que los casos personalizados son mejores",
      whyTitle: "Ya conoces a los sospechosos.",
      whyBody:
        "Un caso prefabricado te pide que te importen extranos. Un caso personalizado construye una historia con personas que ya conoces.",
      moments: [
        {
          title: "Noche de cita",
          text: "Convierte a los dos en el centro de un misterio mas personal que una cena y pelicula.",
        },
        {
          title: "Sorpresa de cumpleanos",
          text: "Regala un expediente donde sus amigos se convierten en sospechosos y testigos.",
        },
        {
          title: "Noche de juegos con amigos",
          text: "Usa el sistema integrado de compartir para invitar al grupo a jugar contigo.",
        },
        {
          title: "Regalo o aniversario",
          text: "Entrega algo hecho para la persona en lugar de otro juego generico.",
        },
      ],
      citySpecificTitle: "Ambiente de ciudad real",
      citySpecificBody: "El escenario se siente familiar en vez de anonimo.",
      relationshipTitle: "Pistas basadas en relaciones",
      relationshipBody:
        "Las personas del expediente ya tienen historia entre si.",
      detectivesEyebrow: "Detectives reales",
      detectivesTitle: "Casos cerrados. Sospechosos acusados.",
      testimonials: [
        {
          quote:
            "Mis amigos no podian creer que ellos fueran los sospechosos. Pasamos horas acusandonos. El mejor regalo de cumpleanos.",
          name: "Sarah M.",
          location: "Austin, TX",
          tag: "Cumpleanos",
        },
        {
          quote:
            "Mucho mejor que otra escape room. Tener nuestra relacion real de fondo lo hizo personal y divertidisimo.",
          name: "Jake & Emma",
          location: "Portland, OR",
          tag: "Cita",
        },
        {
          quote:
            "Fue facil introducir los detalles y luego nos divertimos muchisimo jugando el caso.",
          name: "Marcus T.",
          location: "Chicago, IL",
          tag: "Noche de juego",
        },
      ],
      summaryLabels: [
        "Casos creados este mes",
        "Valoracion media",
        "Tiempo medio de creacion",
        "Garantia de satisfaccion",
      ],
      faqEyebrow: "Preguntas respondidas",
      faqTitle: "Antes de comprar",
      faqBody:
        "Respuestas rapidas para resolver las dudas que suelen frenar la compra.",
      faqs: [
        {
          question: "Necesito todos los detalles antes de comprar?",
          answer:
            "No. Puedes completar el formulario mas tarde. Despues de comprar, puedes volver para terminar ciudad, sospechosos y la historia.",
        },
        {
          question: "Que recibo exactamente?",
          answer:
            "Un expediente digital estructurado con contexto, perfiles de sospechosos, pruebas, emails, mensajes, autopsia, falsas pistas y solucion final.",
        },
        {
          question: "Puedo volver mas tarde para completar el formulario?",
          answer:
            "Si. El formulario del caso personalizado se puede retomar desde este navegador.",
        },
        {
          question: "Es diferente de un misterio prefabricado?",
          answer:
            "Si. Los casos prefabricados ya vienen cerrados. Este usa tu ciudad y tus personas como sospechosos y luego eliges entre 3 historias.",
        },
        {
          question: "Cuanto tarda en llegar mi caso?",
          answer:
            "El pago es inmediato. Una vez envias el formulario, tu caso final se genera y se entrega en tu enlace privado en unos 5 minutos.",
        },
        {
          question: "Puedo imprimirlo o tengo que leerlo en pantalla?",
          answer:
            "Las dos cosas. El caso esta optimizado para pantalla, pero tambien se puede imprimir.",
        },
        {
          question: "Puedo compartir el caso con amigos?",
          answer:
            "Si, mediante un enlace de invitado. Lo que no esta permitido es revenderlo o cobrar acceso.",
        },
      ],
      plannerStatusLabel: "Estado del borrador",
      suspectsLabel: "Sospechosos",
      addUpTo: "Anade hasta 6 sospechosos",
      cityLabel: "Ciudad",
      secureViaFastSpring: "Pago seguro con FastSpring",
      instantDelivery: "Entrega instantanea",
      buildersSuffix: "personas estan creando un caso ahora mismo",
    };
  }

  if (lang === "de") {
    return {
      trustBadges: [
        "Sicherer Checkout",
        "Sofortige Lieferung",
        "Fur 2 bis 6 Spieler",
        "Digitales PDF",
      ],
      panelBenefits: [
        "Personalisierte Fallakte mit deiner Stadt",
        "Verdachtigenprofile mit euren Namen",
        "Individuelle Hinweise und falsche Fahrten",
        "Druckbar oder am Bildschirm spielbar",
        "Komplette Losung enthalten",
      ],
      oneTime: "Einmalig",
      noSubscription: "Kein Abo",
      panelHowItWorks: "So funktioniert es",
      deliverySteps: [
        "Sichere Zahlung ausfuhren.",
        "Stadt und Verdachtige eingeben.",
        "Die passende Story auswahlen.",
        "Den Fall generieren.",
      ],
      howItWorksEyebrow: "So funktioniert es",
      howItWorksTitle: "Vom Checkout zur Fallakte in wenigen Minuten",
      howItWorks: [
        {
          title: "Fall kaufen",
          text: "Einfacher Checkout. Keine langen Formulare vor dem Kauf.",
        },
        {
          title: "Briefing ausfullen",
          text: "Stadt, Namen, Beziehungen und Storyrichtung eingeben.",
        },
        {
          title: "Wir bauen ihn",
          text: "Deine Angaben werden zu Hinweisen, Motiven und falschen Fahrten.",
        },
        {
          title: "Fall losen",
          text: "Erhalte deine private Fallakte zum Lesen oder Ausdrucken.",
        },
      ],
      deliverablesEyebrow: "Was du bekommst",
      deliverablesTitle: "Jeder Fall wird fur euch gebaut.",
      deliverablesBody:
        "Unser AI Wizard nimmt deine Stadt, deine Verdachtigen und die gewahlte Story und baut wahrend des Wartens einen kompletten Fall.",
      deliverablesCta: "Fall starten",
      deliverables: [
        {
          title: "Verdachtigenprofile",
          text: "Detaillierte Profile rund um eure Personen, Beziehungen und Berufe.",
        },
        {
          title: "Beweispaket",
          text: "Hinweise, falsche Fahrten und Ermittlungsnotizen wie in einer echten Akte.",
        },
        {
          title: "Motivnetz",
          text: "Eine Storystruktur, die echte Beziehungen in glaubwurdigen Verdacht verwandelt.",
        },
        {
          title: "Finale Auflosung",
          text: "Eine klare Losung fur ein starkes Ende, freigeschaltet nach dem Haftbefehl.",
        },
      ],
      whyEyebrow: "Warum individuelle Falle besser sind",
      whyTitle: "Ihr kennt die Verdachtigen bereits.",
      whyBody:
        "Ein vorgefertigter Fall verlangt Interesse an Fremden. Ein individueller Fall baut auf Menschen auf, die ihr schon kennt.",
      moments: [
        {
          title: "Date-Abend",
          text: "Macht euch beide zum Mittelpunkt eines Falls, der personlicher ist als Dinner und Kino.",
        },
        {
          title: "Geburtstagsuberraschung",
          text: "Schenke eine Akte, in der Freunde zu Verdachtigen und Zeugen werden.",
        },
        {
          title: "Spieleabend mit Freunden",
          text: "Nutze die integrierte Freigabe, um gemeinsam mit Freunden zu ermitteln.",
        },
        {
          title: "Jahrestag oder Geschenk",
          text: "Ubergib etwas, das wirklich fur die Person gemacht wurde.",
        },
      ],
      citySpecificTitle: "Atmosphare aus eurer Stadt",
      citySpecificBody: "Der Schauplatz wirkt vertraut statt anonym.",
      relationshipTitle: "Hinweise aus Beziehungen",
      relationshipBody:
        "Die Menschen in der Akte haben bereits eine Geschichte miteinander.",
      detectivesEyebrow: "Echte Detektive",
      detectivesTitle: "Falle gelost. Verdachtige beschuldigt.",
      testimonials: [
        {
          quote:
            "Meine Freunde konnten nicht glauben, dass sie die Verdachtigen waren. Wir haben uns stundenlang beschuldigt. Das beste Geburtstagsgeschenk.",
          name: "Sarah M.",
          location: "Austin, TX",
          tag: "Geburtstag",
        },
        {
          quote:
            "Viel besser als noch ein Escape Room. Unsere echte Beziehung als Hintergrund machte alles personlich und lustig.",
          name: "Jake & Emma",
          location: "Portland, OR",
          tag: "Date-Abend",
        },
        {
          quote:
            "Es war super einfach, die Details einzugeben, und danach hatten wir riesigen Spass mit dem Fall.",
          name: "Marcus T.",
          location: "Chicago, IL",
          tag: "Spieleabend",
        },
      ],
      summaryLabels: [
        "Falle diesen Monat",
        "Durchschnittsbewertung",
        "Durchschnittliche Bauzeit",
        "Zufriedenheitsgarantie",
      ],
      faqEyebrow: "Fragen beantwortet",
      faqTitle: "Vor dem Kauf",
      faqBody:
        "Kurze Antworten auf die Fragen, die einen individuellen Kauf oft bremsen.",
      faqs: [
        {
          question: "Muss ich vor dem Kauf schon alle Details haben?",
          answer:
            "Nein. Du kannst den Intake spater abschliessen. Nach dem Kauf kannst du fur Stadt, Verdachtige und Storywahl zuruckkehren.",
        },
        {
          question: "Was bekomme ich genau?",
          answer:
            "Eine strukturierte digitale Fallakte mit Hintergrund, Verdachtigenprofilen, Beweisen, E-Mails, Nachrichten, Autopsie, falschen Fahrten und finaler Losung.",
        },
        {
          question: "Kann ich spater zuruckkommen und den Intake beenden?",
          answer:
            "Ja. Der individuelle Intake kann in diesem Browser fortgesetzt werden.",
        },
        {
          question: "Ist das anders als ein vorgefertigter Krimifall?",
          answer:
            "Ja. Vorgefertigte Falle sind fix. Dieser nutzt deine Stadt und eure Personen als Verdachtige und bietet dann 3 Storyrichtungen.",
        },
        {
          question: "Wie lange dauert es, bis ich meinen Fall bekomme?",
          answer:
            "Der Checkout ist sofort. Nach dem eingesendeten Intake wird der Fall gestartet und in etwa 5 Minuten uber den privaten Link bereitgestellt.",
        },
        {
          question: "Kann ich ihn ausdrucken oder nur am Bildschirm lesen?",
          answer:
            "Beides. Der Fall ist fur Bildschirm optimiert, lasst sich aber auch ausdrucken.",
        },
        {
          question: "Kann ich den Fall mit Freunden teilen?",
          answer:
            "Ja, uber einen Gastlink. Nicht erlaubt ist allerdings der Weiterverkauf oder das Berechnen von Zugang.",
        },
      ],
      plannerStatusLabel: "Entwurfsstatus",
      suspectsLabel: "Verdachtige",
      addUpTo: "Bis zu 6 Verdachtige hinzufugen",
      cityLabel: "Stadt",
      secureViaFastSpring: "Sicherer Checkout mit FastSpring",
      instantDelivery: "Sofortige Lieferung",
      buildersSuffix: "Personen bauen gerade einen Fall",
    };
  }

  if (lang === "it") {
    return {
      trustBadges: [
        "Pagamento sicuro",
        "Consegna immediata",
        "Pensato per 2-6 giocatori",
        "PDF digitale",
      ],
      panelBenefits: [
        "Fascicolo personalizzato con la tua citta",
        "Profili dei sospetti con i vostri nomi",
        "Indizi e false piste su misura",
        "Stampabile o giocabile su schermo",
        "Soluzione finale inclusa",
      ],
      oneTime: "Una tantum",
      noSubscription: "Nessun abbonamento",
      panelHowItWorks: "Come funziona",
      deliverySteps: [
        "Effettua un pagamento sicuro.",
        "Inserisci citta e sospetti.",
        "Scegli la storia che preferisci.",
        "Genera il caso.",
      ],
      howItWorksEyebrow: "Come funziona",
      howItWorksTitle: "Dal checkout al fascicolo finale in pochi minuti",
      howItWorks: [
        {
          title: "Acquista il caso",
          text: "Checkout semplice. Nessun modulo lungo prima dell'acquisto.",
        },
        {
          title: "Compila il brief",
          text: "Aggiungi citta, nomi, relazioni e scegli l'intreccio.",
        },
        {
          title: "Lo costruiamo",
          text: "I tuoi dettagli diventano indizi, moventi e false piste.",
        },
        {
          title: "Risolvi il caso",
          text: "Ricevi il fascicolo privato da leggere su schermo o da stampare.",
        },
      ],
      deliverablesEyebrow: "Cosa ricevi",
      deliverablesTitle: "Ogni caso e costruito per voi.",
      deliverablesBody:
        "Il nostro AI Wizard prende la tua citta, i tuoi sospetti e la storia scelta e costruisce un mistero completo mentre aspetti.",
      deliverablesCta: "Avvia il caso",
      deliverables: [
        {
          title: "Profili dei sospetti",
          text: "Schede dettagliate basate sulle persone scelte, le loro relazioni e professioni.",
        },
        {
          title: "Pacchetto prove",
          text: "Indizi, false piste e note investigative organizzate come un vero fascicolo.",
        },
        {
          title: "Rete dei moventi",
          text: "Una struttura narrativa che trasforma relazioni reali in sospetti credibili.",
        },
        {
          title: "Rivelazione finale",
          text: "Una soluzione chiara per una chiusura soddisfacente, sbloccata con il mandato di arresto.",
        },
      ],
      whyEyebrow: "Perche i casi personalizzati sono migliori",
      whyTitle: "Conoscete gia i sospetti.",
      whyBody:
        "Un caso preconfezionato ti chiede di interessarti a sconosciuti. Un caso personalizzato costruisce la storia su persone che gia conosci.",
      moments: [
        {
          title: "Serata romantica",
          text: "Mettete voi due al centro di un mistero piu personale di una cena e film.",
        },
        {
          title: "Sorpresa di compleanno",
          text: "Regala un fascicolo in cui gli amici diventano sospetti e testimoni.",
        },
        {
          title: "Serata giochi tra amici",
          text: "Usa la condivisione integrata per invitare gli amici a indagare con te.",
        },
        {
          title: "Anniversario o regalo",
          text: "Consegna qualcosa fatto davvero per la persona, non un altro gioco generico.",
        },
      ],
      citySpecificTitle: "Atmosfera legata alla tua citta",
      citySpecificBody: "L'ambientazione sembra familiare invece che anonima.",
      relationshipTitle: "Indizi basati sulle relazioni",
      relationshipBody:
        "Le persone nel fascicolo hanno gia una storia tra loro.",
      detectivesEyebrow: "Veri detective",
      detectivesTitle: "Casi chiusi. Sospetti accusati.",
      testimonials: [
        {
          quote:
            "I miei amici non riuscivano a credere di essere i sospetti. Ci siamo accusati per ore. Il miglior regalo di compleanno.",
          name: "Sarah M.",
          location: "Austin, TX",
          tag: "Compleanno",
        },
        {
          quote:
            "Molto meglio dell'ennesima escape room. Avere la nostra vera relazione sullo sfondo lo ha reso personale e divertente.",
          name: "Jake & Emma",
          location: "Portland, OR",
          tag: "Serata romantica",
        },
        {
          quote:
            "E stato facilissimo inserire i dettagli e poi ci siamo divertiti tantissimo a giocare il caso.",
          name: "Marcus T.",
          location: "Chicago, IL",
          tag: "Serata giochi",
        },
      ],
      summaryLabels: [
        "Casi creati questo mese",
        "Valutazione media",
        "Tempo medio di creazione",
        "Garanzia soddisfatti",
      ],
      faqEyebrow: "Domande e risposte",
      faqTitle: "Prima di acquistare",
      faqBody:
        "Risposte rapide ai dubbi che spesso rallentano l'acquisto di un caso personalizzato.",
      faqs: [
        {
          question: "Devo avere tutti i dettagli prima di acquistare?",
          answer:
            "No. Puoi completare l'intake in un secondo momento. Dopo l'acquisto puoi tornare per citta, sospetti e scelta della storia.",
        },
        {
          question: "Cosa ricevo esattamente?",
          answer:
            "Un fascicolo digitale strutturato con contesto, profili dei sospetti, prove, email, messaggi, autopsia, false piste e soluzione finale.",
        },
        {
          question: "Posso tornare piu tardi per completare l'intake?",
          answer:
            "Si. L'intake del caso personalizzato e riprendibile da questo browser.",
        },
        {
          question: "E diverso da un mistero gia pronto?",
          answer:
            "Si. I casi pronti sono fissi. Questo usa la tua citta e le tue persone come sospetti e poi ti fa scegliere tra 3 direzioni narrative.",
        },
        {
          question: "Quanto tempo serve per ricevere il mio caso?",
          answer:
            "Il checkout e immediato. Una volta inviato l'intake, il caso finale viene costruito e consegnato sul link privato in circa 5 minuti.",
        },
        {
          question: "Posso stamparlo o devo leggerlo solo a schermo?",
          answer:
            "Entrambe le cose. Il caso e ottimizzato per schermo ma puo anche essere stampato.",
        },
        {
          question: "Posso condividere il caso con gli amici?",
          answer:
            "Si, tramite link ospite. Non e consentito rivenderlo o far pagare l'accesso.",
        },
      ],
      plannerStatusLabel: "Stato della bozza",
      suspectsLabel: "Sospetti",
      addUpTo: "Aggiungi fino a 6 sospetti",
      cityLabel: "Citta",
      secureViaFastSpring: "Pagamento sicuro con FastSpring",
      instantDelivery: "Consegna immediata",
      buildersSuffix: "persone stanno creando un caso in questo momento",
    };
  }

  return {
    trustBadges: TRUST_BADGES.map((entry) => entry.label),
    panelBenefits: [
      "Personalized case file with your city",
      "Suspect dossiers with your names",
      "Custom clues and red herrings",
      "Printable or play on-screen",
      "Complete solution included",
    ],
    oneTime: "One-time",
    noSubscription: "No subscription",
    panelHowItWorks: "How It Works",
    deliverySteps: DELIVERY_STEPS,
    howItWorksEyebrow: "How It Works",
    howItWorksTitle: "From checkout to case file in minutes",
    howItWorks: HOW_IT_WORKS.map(({ title, text }) => ({ title, text })),
    deliverablesEyebrow: "What You Receive",
    deliverablesTitle: "Every Case is Custom for You.",
    deliverablesBody:
      "Our AI Wizard takes your city, your suspects and the story you pick and then builds a custom mystery case for you while you wait.",
    deliverablesCta: "Start the case",
    deliverables: DELIVERABLES.map(({ title, text }) => ({ title, text })),
    whyEyebrow: "Why Custom Cases Are Better",
    whyTitle: "You already know the suspects.",
    whyBody:
      "A premade case asks you to care about strangers. A custom case builds a story with people you already know, then you have to play to figure out who did it.",
    moments: MOMENT_USE_CASES,
    citySpecificTitle: "City-specific atmosphere",
    citySpecificBody: "The setting feels familiar instead of anonymous.",
    relationshipTitle: "Relationship-driven clues",
    relationshipBody: "The people in the file already have history.",
    detectivesEyebrow: "Real Detectives",
    detectivesTitle: "Cases closed. Suspects accused.",
    testimonials: TESTIMONIALS,
    summaryLabels: SUMMARY_STATS.map((entry) => entry.label),
    faqEyebrow: "Questions Answered",
    faqTitle: "Before you buy",
    faqBody:
      "Quick answers to handle the doubts that usually slow down a custom purchase.",
    faqs: FAQS,
    plannerStatusLabel: "Draft Status",
    suspectsLabel: "Suspects",
    addUpTo: "Add up to 6 suspects",
    cityLabel: "City",
    secureViaFastSpring: "Secure checkout via FastSpring",
    instantDelivery: "Instant delivery",
    buildersSuffix: "people are building a case right now",
  };
};

const buildLocalizedFormCopy = (
  lang: keyof typeof CUSTOM_ENTRY_COPY,
): CustomCaseFormCopy | undefined => {
  if (lang === "fr") {
    return {
      firstNameLabel: "Prenom",
      firstNamePlaceholder: "Votre prenom",
      firstNameHelp: "Nous l'utilisons dans l'accueil audio et la livraison.",
      emailLabel: "Email",
      emailPlaceholder: "vous@email.com",
      sourceLabel: "Ou avez-vous entendu parler de nous ?",
      sourcePlaceholder: "Selectionnez une option",
      sourceOptions: [
        { value: "Google", label: "Google" },
        { value: "YouTube", label: "YouTube" },
        { value: "TikTok", label: "TikTok" },
        { value: "Instagram", label: "Instagram" },
        { value: "Pinterest", label: "Pinterest" },
        { value: "Other", label: "Autre" },
      ],
      cityLabel: "Ville",
      cityPlaceholder: "Entrez le nom de la ville",
      locationLabel: "Lieu (optionnel)",
      locationPlaceholder: "Lieu precis dans la ville",
      suspectsLabel: "Suspects (max 6)",
      suspectsHelp:
        "Entrez uniquement les suspects. N'ajoutez pas la victime ici. Utilisez des prenoms puis ajoutez professions, relations et details d'apparence.",
      suspectsMissing: "Ajoutez au moins un suspect avant l'envoi.",
      devPresetTitle: "Presets de test dev",
      devPresetDescription:
        "Remplacez la liste de suspects en un clic pour tester plus vite en local.",
      suspectCardTitle: "Suspect",
      suspectMissingBadge: "Details manquants",
      suspectFirstNameLabel: "Prenom",
      suspectFirstNamePlaceholder: "Jean",
      ageLabel: "Age",
      agePlaceholder: "30",
      eyesLabel: "Yeux",
      eyesOptions: [
        { value: "Light", label: "Clairs" },
        { value: "Dark", label: "Fonces" },
      ],
      heightLabel: "Taille",
      heightOptions: [
        { value: "Short", label: "Petit" },
        { value: "Average", label: "Moyen" },
        { value: "Tall", label: "Grand" },
      ],
      sexLabel: "Sexe",
      sexOptions: [
        { value: "M", label: "Homme" },
        { value: "F", label: "Femme" },
        { value: "N/A", label: "Prefere ne pas preciser" },
      ],
      hairColorLabel: "Couleur des cheveux",
      hairColorOptions: [
        { value: "Black", label: "Noir" },
        { value: "Brown", label: "Brun" },
        { value: "Blonde", label: "Blond" },
        { value: "Red", label: "Roux" },
        { value: "Gray", label: "Gris" },
        { value: "Bald", label: "Chauve" },
        { value: "Other", label: "Autre" },
      ],
      hairLengthLabel: "Longueur des cheveux",
      hairLengthOptions: [
        { value: "Short", label: "Courts" },
        { value: "Medium", label: "Moyens" },
        { value: "Long", label: "Longs" },
        { value: "Bald", label: "Chauve" },
      ],
      skinToneLabel: "Teinte de peau",
      skinToneOptions: [
        { value: "Light", label: "Claire" },
        { value: "Medium", label: "Moyenne" },
        { value: "Dark", label: "Foncee" },
        { value: "Other", label: "Autre" },
        { value: "Prefer not to specify", label: "Prefere ne pas preciser" },
      ],
      occupationLabel: "Profession",
      occupationPlaceholder: "Developpeur logiciel",
      relationshipLabel: "Relation (optionnel)",
      relationshipPlaceholder: "Ami de la victime",
      notesLabel: "Notes supplementaires (optionnel)",
      notesPlaceholder: "Tout autre detail pour personnaliser l'histoire.",
      addSuspectEmpty:
        "Ajoutez au moins un suspect. Vous pouvez en inclure jusqu'a six.",
      addSuspectMax: "Nombre maximal de suspects atteint (6)",
      addSuspectButton: "Ajouter un suspect",
      selectPlaceholder: "Selectionnez...",
    };
  }

  if (lang === "es") {
    return {
      firstNameLabel: "Nombre",
      firstNamePlaceholder: "Tu nombre",
      firstNameHelp: "Lo usamos en la bienvenida en audio y en la entrega.",
      emailLabel: "Correo",
      emailPlaceholder: "tu@email.com",
      sourceLabel: "Donde nos conociste?",
      sourcePlaceholder: "Selecciona una opcion",
      sourceOptions: [
        { value: "Google", label: "Google" },
        { value: "YouTube", label: "YouTube" },
        { value: "TikTok", label: "TikTok" },
        { value: "Instagram", label: "Instagram" },
        { value: "Pinterest", label: "Pinterest" },
        { value: "Other", label: "Otro" },
      ],
      cityLabel: "Ciudad",
      cityPlaceholder: "Introduce el nombre de la ciudad",
      locationLabel: "Ubicacion (opcional)",
      locationPlaceholder: "Lugar concreto dentro de la ciudad",
      suspectsLabel: "Sospechosos (max. 6)",
      suspectsHelp:
        "Introduce solo a los sospechosos. No pongas a la victima aqui. Usa nombres de pila y despues anade profesiones, relaciones y rasgos fisicos.",
      suspectsMissing: "Anade al menos un sospechoso antes de enviar.",
      devPresetTitle: "Presets de prueba",
      devPresetDescription:
        "Sustituye la lista de sospechosos con un clic para probar mas rapido en local.",
      suspectCardTitle: "Sospechoso",
      suspectMissingBadge: "Faltan datos",
      suspectFirstNameLabel: "Nombre",
      suspectFirstNamePlaceholder: "Juan",
      ageLabel: "Edad",
      agePlaceholder: "30",
      eyesLabel: "Ojos",
      eyesOptions: [
        { value: "Light", label: "Claros" },
        { value: "Dark", label: "Oscuros" },
      ],
      heightLabel: "Altura",
      heightOptions: [
        { value: "Short", label: "Baja" },
        { value: "Average", label: "Media" },
        { value: "Tall", label: "Alta" },
      ],
      sexLabel: "Sexo",
      sexOptions: [
        { value: "M", label: "Hombre" },
        { value: "F", label: "Mujer" },
        { value: "N/A", label: "Prefiero no indicarlo" },
      ],
      hairColorLabel: "Color de pelo",
      hairColorOptions: [
        { value: "Black", label: "Negro" },
        { value: "Brown", label: "Castano" },
        { value: "Blonde", label: "Rubio" },
        { value: "Red", label: "Pelirrojo" },
        { value: "Gray", label: "Gris" },
        { value: "Bald", label: "Calvo" },
        { value: "Other", label: "Otro" },
      ],
      hairLengthLabel: "Largo del pelo",
      hairLengthOptions: [
        { value: "Short", label: "Corto" },
        { value: "Medium", label: "Medio" },
        { value: "Long", label: "Largo" },
        { value: "Bald", label: "Calvo" },
      ],
      skinToneLabel: "Tono de piel",
      skinToneOptions: [
        { value: "Light", label: "Claro" },
        { value: "Medium", label: "Medio" },
        { value: "Dark", label: "Oscuro" },
        { value: "Other", label: "Otro" },
        { value: "Prefer not to specify", label: "Prefiero no indicarlo" },
      ],
      occupationLabel: "Profesion",
      occupationPlaceholder: "Desarrollador de software",
      relationshipLabel: "Relacion (opcional)",
      relationshipPlaceholder: "Amigo de la victima",
      notesLabel: "Notas extra (opcional)",
      notesPlaceholder: "Cualquier detalle que haga la historia mas personal.",
      addSuspectEmpty:
        "Anade al menos un sospechoso. Puedes incluir hasta seis.",
      addSuspectMax: "Numero maximo de sospechosos alcanzado (6)",
      addSuspectButton: "Anadir sospechoso",
      selectPlaceholder: "Selecciona...",
    };
  }

  if (lang === "de") {
    return {
      firstNameLabel: "Vorname",
      firstNamePlaceholder: "Dein Vorname",
      firstNameHelp: "Wir verwenden ihn fur Audio-Begrussung und Zustellung.",
      emailLabel: "E-Mail",
      emailPlaceholder: "du@email.com",
      sourceLabel: "Woher kennst du uns?",
      sourcePlaceholder: "Bitte wahlen",
      sourceOptions: [
        { value: "Google", label: "Google" },
        { value: "YouTube", label: "YouTube" },
        { value: "TikTok", label: "TikTok" },
        { value: "Instagram", label: "Instagram" },
        { value: "Pinterest", label: "Pinterest" },
        { value: "Other", label: "Andere" },
      ],
      cityLabel: "Stadt",
      cityPlaceholder: "Name der Stadt eingeben",
      locationLabel: "Ort (optional)",
      locationPlaceholder: "Genauer Ort innerhalb der Stadt",
      suspectsLabel: "Verdachtige (max. 6)",
      suspectsHelp:
        "Bitte nur Verdachtige eintragen. Nicht das Opfer hier eintragen. Verwende Vornamen und erganze dann Beruf, Beziehung und Aussehen.",
      suspectsMissing:
        "Bitte vor dem Absenden mindestens eine verdachtige Person hinzufugen.",
      devPresetTitle: "Dev-Test-Presets",
      devPresetDescription:
        "Ersetze die Verdachtigenliste mit einem Klick fur schnellere lokale Tests.",
      suspectCardTitle: "Verdachtige Person",
      suspectMissingBadge: "Angaben fehlen",
      suspectFirstNameLabel: "Vorname",
      suspectFirstNamePlaceholder: "Johann",
      ageLabel: "Alter",
      agePlaceholder: "30",
      eyesLabel: "Augen",
      eyesOptions: [
        { value: "Light", label: "Hell" },
        { value: "Dark", label: "Dunkel" },
      ],
      heightLabel: "Grosse",
      heightOptions: [
        { value: "Short", label: "Klein" },
        { value: "Average", label: "Mittel" },
        { value: "Tall", label: "Gross" },
      ],
      sexLabel: "Geschlecht",
      sexOptions: [
        { value: "M", label: "Mannlich" },
        { value: "F", label: "Weiblich" },
        { value: "N/A", label: "Mochte ich nicht angeben" },
      ],
      hairColorLabel: "Haarfarbe",
      hairColorOptions: [
        { value: "Black", label: "Schwarz" },
        { value: "Brown", label: "Braun" },
        { value: "Blonde", label: "Blond" },
        { value: "Red", label: "Rot" },
        { value: "Gray", label: "Grau" },
        { value: "Bald", label: "Glatze" },
        { value: "Other", label: "Andere" },
      ],
      hairLengthLabel: "Haarlange",
      hairLengthOptions: [
        { value: "Short", label: "Kurz" },
        { value: "Medium", label: "Mittel" },
        { value: "Long", label: "Lang" },
        { value: "Bald", label: "Glatze" },
      ],
      skinToneLabel: "Hautton",
      skinToneOptions: [
        { value: "Light", label: "Hell" },
        { value: "Medium", label: "Mittel" },
        { value: "Dark", label: "Dunkel" },
        { value: "Other", label: "Andere" },
        { value: "Prefer not to specify", label: "Mochte ich nicht angeben" },
      ],
      occupationLabel: "Beruf",
      occupationPlaceholder: "Softwareentwickler",
      relationshipLabel: "Beziehung (optional)",
      relationshipPlaceholder: "Freund des Opfers",
      notesLabel: "Zusatzliche Notizen (optional)",
      notesPlaceholder: "Alles, was die Geschichte personlicher macht.",
      addSuspectEmpty:
        "Bitte mindestens eine verdachtige Person hinzufugen. Bis zu sechs sind moglich.",
      addSuspectMax: "Maximale Anzahl an Verdachtigen erreicht (6)",
      addSuspectButton: "Verdachtige Person hinzufugen",
      selectPlaceholder: "Auswahlen...",
    };
  }

  if (lang === "it") {
    return {
      firstNameLabel: "Nome",
      firstNamePlaceholder: "Il tuo nome",
      firstNameHelp: "Lo usiamo nell'audio di benvenuto e nella consegna.",
      emailLabel: "Email",
      emailPlaceholder: "tu@email.com",
      sourceLabel: "Dove ci hai conosciuti?",
      sourcePlaceholder: "Seleziona un'opzione",
      sourceOptions: [
        { value: "Google", label: "Google" },
        { value: "YouTube", label: "YouTube" },
        { value: "TikTok", label: "TikTok" },
        { value: "Instagram", label: "Instagram" },
        { value: "Pinterest", label: "Pinterest" },
        { value: "Other", label: "Altro" },
      ],
      cityLabel: "Citta",
      cityPlaceholder: "Inserisci il nome della citta",
      locationLabel: "Luogo (opzionale)",
      locationPlaceholder: "Luogo preciso nella citta",
      suspectsLabel: "Sospetti (max 6)",
      suspectsHelp:
        "Inserisci solo i sospetti. Non inserire qui la vittima. Usa i nomi di battesimo e poi aggiungi professione, relazione e dettagli fisici.",
      suspectsMissing: "Aggiungi almeno un sospetto prima di inviare.",
      devPresetTitle: "Preset di test dev",
      devPresetDescription:
        "Sostituisci la lista dei sospetti con un clic per test locali piu rapidi.",
      suspectCardTitle: "Sospetto",
      suspectMissingBadge: "Dettagli mancanti",
      suspectFirstNameLabel: "Nome",
      suspectFirstNamePlaceholder: "Giulia",
      ageLabel: "Eta",
      agePlaceholder: "30",
      eyesLabel: "Occhi",
      eyesOptions: [
        { value: "Light", label: "Chiari" },
        { value: "Dark", label: "Scuri" },
      ],
      heightLabel: "Altezza",
      heightOptions: [
        { value: "Short", label: "Bassa" },
        { value: "Average", label: "Media" },
        { value: "Tall", label: "Alta" },
      ],
      sexLabel: "Sesso",
      sexOptions: [
        { value: "M", label: "Maschile" },
        { value: "F", label: "Femminile" },
        { value: "N/A", label: "Preferisco non specificarlo" },
      ],
      hairColorLabel: "Colore dei capelli",
      hairColorOptions: [
        { value: "Black", label: "Nero" },
        { value: "Brown", label: "Castano" },
        { value: "Blonde", label: "Biondo" },
        { value: "Red", label: "Rosso" },
        { value: "Gray", label: "Grigio" },
        { value: "Bald", label: "Calvo" },
        { value: "Other", label: "Altro" },
      ],
      hairLengthLabel: "Lunghezza dei capelli",
      hairLengthOptions: [
        { value: "Short", label: "Corti" },
        { value: "Medium", label: "Medi" },
        { value: "Long", label: "Lunghi" },
        { value: "Bald", label: "Calvo" },
      ],
      skinToneLabel: "Tono della pelle",
      skinToneOptions: [
        { value: "Light", label: "Chiaro" },
        { value: "Medium", label: "Medio" },
        { value: "Dark", label: "Scuro" },
        { value: "Other", label: "Altro" },
        {
          value: "Prefer not to specify",
          label: "Preferisco non specificarlo",
        },
      ],
      occupationLabel: "Professione",
      occupationPlaceholder: "Sviluppatore software",
      relationshipLabel: "Relazione (opzionale)",
      relationshipPlaceholder: "Amico della vittima",
      notesLabel: "Note extra (opzionale)",
      notesPlaceholder:
        "Qualsiasi dettaglio che renda la storia piu personale.",
      addSuspectEmpty:
        "Aggiungi almeno un sospetto. Puoi inserirne fino a sei.",
      addSuspectMax: "Numero massimo di sospetti raggiunto (6)",
      addSuspectButton: "Aggiungi sospetto",
      selectPlaceholder: "Seleziona...",
    };
  }

  return undefined;
};

const buildLocalizedUiCopy = (lang: keyof typeof CUSTOM_ENTRY_COPY) => {
  if (lang === "fr") {
    return {
      casesSolvedThisMonth: "affaires resolues ce mois-ci",
      reviewsLabel: "avis",
      stepLabel: "Etape",
      plannerSubmitLabel: "Enregistrer le brouillon et ouvrir le paiement",
      policyNoticeBeforeTerms: "J'accepte les ",
      termsLabel: "Conditions",
      policyNoticeBeforePrivacy: " et la ",
      privacyLabel: "Politique de confidentialite",
      policyNoticeAfterPrivacy:
        ", et je comprends que vous m'ecrirez au sujet de l'acces, de la livraison et du support pour cet achat.",
      devBypassIdle: "Ignorer le paiement en dev",
      devBypassBusy: "Ignorons le paiement...",
      startFreshIntake: "Commencer un nouveau brief",
      runtime: {
        paymentReceivedOpeningIntake:
          "Paiement recu. Ouverture de votre brief prive...",
        checkoutClosedBeforePayment:
          "Le paiement a ete ferme avant confirmation. Relancez le paiement securise quand vous etes pret.",
        enterEmailBeforeCheckout:
          "Entrez votre email avant de lancer le paiement.",
        acceptPolicyBeforeCheckout:
          "Veuillez accepter l'avis de confidentialite et d'acces avant de lancer le paiement.",
        preparingSecureCheckout: "Preparation du paiement securise...",
        fastspringUnavailable:
          "Le paiement FastSpring n'est pas disponible pour le moment.",
        checkoutNotReady: "Le paiement n'est pas pret pour le moment.",
        checkoutOpened:
          "Le paiement est ouvert. Une fois termine, nous vous renverrons automatiquement vers votre brief prive.",
        checkoutStartFailed: "Nous n'avons pas pu lancer le paiement.",
        freshCaseReady: "Pret a commencer une nouvelle affaire personnalisee.",
        enterEmailBeforeDevBypass:
          "Entrez votre email avant d'ignorer le paiement en dev.",
        acceptPolicyBeforeDevBypass:
          "Veuillez accepter l'avis de confidentialite et d'acces avant d'ignorer le paiement en dev.",
        creatingDevTestOrder: "Creation d'une commande de test dev...",
        applyingDevPaymentBypass: "Application du bypass de paiement dev...",
        unlockingOwnerAccess:
          "Ouverture de l'acces proprietaire pour ce navigateur...",
        devBypassAppliedOpeningIntake:
          "Bypass dev applique. Ouverture de votre brief...",
        devBypassFailed: "Nous n'avons pas pu creer une commande de test dev.",
        unlockingSavedIntake:
          "Ouverture de votre brief enregistre pour ce navigateur...",
        savedIntakeUnlocked: "Brief enregistre debloque. Ouverture en cours...",
        savedIntakeOpenFailed:
          "Nous n'avons pas pu ouvrir le brief enregistre.",
        prepareCheckoutFallback:
          "Impossible de preparer le paiement pour le moment.",
        devAccessCodeUnavailable:
          "Le code d'acces de previsualisation dev n'etait pas disponible.",
        devBypassFallback:
          "Impossible d'ignorer le paiement en dev pour le moment.",
        savedIntakeFallback:
          "Impossible de debloquer le brief enregistre en dev.",
      },
    };
  }

  if (lang === "es") {
    return {
      casesSolvedThisMonth: "casos resueltos este mes",
      reviewsLabel: "resenas",
      stepLabel: "Paso",
      plannerSubmitLabel: "Guardar borrador y abrir el pago",
      policyNoticeBeforeTerms: "Acepto los ",
      termsLabel: "Terminos",
      policyNoticeBeforePrivacy: " y la ",
      privacyLabel: "Politica de Privacidad",
      policyNoticeAfterPrivacy:
        ", y entiendo que me escribireis sobre acceso al pedido, entrega y soporte de esta compra.",
      devBypassIdle: "Omitir pago en dev",
      devBypassBusy: "Omitiendo pago...",
      startFreshIntake: "Empezar un formulario nuevo",
      runtime: {
        paymentReceivedOpeningIntake:
          "Pago recibido. Abriendo tu formulario privado...",
        checkoutClosedBeforePayment:
          "El pago se cerro antes de confirmarse. Vuelve a abrir el pago seguro cuando quieras.",
        enterEmailBeforeCheckout:
          "Introduce tu correo antes de iniciar el pago.",
        acceptPolicyBeforeCheckout:
          "Revisa y acepta el aviso de privacidad y acceso antes de iniciar el pago.",
        preparingSecureCheckout: "Preparando el pago seguro...",
        fastspringUnavailable:
          "El pago de FastSpring no esta disponible ahora mismo.",
        checkoutNotReady: "El pago todavia no esta listo.",
        checkoutOpened:
          "El pago se ha abierto. Cuando termine, te llevaremos automaticamente a tu formulario privado.",
        checkoutStartFailed: "No hemos podido iniciar el pago.",
        freshCaseReady: "Todo listo para empezar un nuevo caso personalizado.",
        enterEmailBeforeDevBypass:
          "Introduce tu correo antes de omitir el pago en dev.",
        acceptPolicyBeforeDevBypass:
          "Revisa y acepta el aviso de privacidad y acceso antes de omitir el pago en dev.",
        creatingDevTestOrder: "Creando un pedido de prueba en dev...",
        applyingDevPaymentBypass: "Aplicando la omision de pago en dev...",
        unlockingOwnerAccess:
          "Desbloqueando el acceso del propietario en este navegador...",
        devBypassAppliedOpeningIntake:
          "Omitido en dev. Abriendo tu formulario...",
        devBypassFailed: "No hemos podido crear un pedido de prueba en dev.",
        unlockingSavedIntake:
          "Desbloqueando tu formulario guardado en este navegador...",
        savedIntakeUnlocked:
          "Formulario guardado desbloqueado. Abriendolo ahora...",
        savedIntakeOpenFailed: "No hemos podido abrir el formulario guardado.",
        prepareCheckoutFallback: "No se puede preparar el pago ahora mismo.",
        devAccessCodeUnavailable:
          "El codigo de acceso de vista previa dev no estaba disponible.",
        devBypassFallback: "No se puede omitir el pago en dev ahora mismo.",
        savedIntakeFallback:
          "No se puede desbloquear el formulario guardado en dev.",
      },
    };
  }

  if (lang === "de") {
    return {
      casesSolvedThisMonth: "Falle in diesem Monat gelost",
      reviewsLabel: "Bewertungen",
      stepLabel: "Schritt",
      plannerSubmitLabel: "Entwurf speichern und Checkout offnen",
      policyNoticeBeforeTerms: "Ich akzeptiere die ",
      termsLabel: "Bedingungen",
      policyNoticeBeforePrivacy: " und die ",
      privacyLabel: "Datenschutzerklarung",
      policyNoticeAfterPrivacy:
        " und verstehe, dass ihr mir zu Bestellzugang, Lieferung und Support fur diesen Kauf schreiben durft.",
      devBypassIdle: "Zahlung in Dev uberspringen",
      devBypassBusy: "Zahlung wird ubersprungen...",
      startFreshIntake: "Neuen Intake starten",
      runtime: {
        paymentReceivedOpeningIntake:
          "Zahlung erhalten. Dein privater Intake wird geoffnet...",
        checkoutClosedBeforePayment:
          "Der Checkout wurde vor Abschluss der Zahlung geschlossen. Offne den sicheren Checkout erneut, wenn du bereit bist.",
        enterEmailBeforeCheckout:
          "Gib deine E-Mail ein, bevor du den Checkout startest.",
        acceptPolicyBeforeCheckout:
          "Bitte akzeptiere den Hinweis zu Datenschutz und Bestellzugang, bevor du den Checkout startest.",
        preparingSecureCheckout: "Sicherer Checkout wird vorbereitet...",
        fastspringUnavailable:
          "Der FastSpring-Checkout ist im Moment nicht verfugbar.",
        checkoutNotReady: "Der Checkout ist noch nicht bereit.",
        checkoutOpened:
          "Der Checkout ist geoffnet. Nach der Zahlung bringen wir dich automatisch zu deinem privaten Intake zuruck.",
        checkoutStartFailed: "Wir konnten den Checkout nicht starten.",
        freshCaseReady: "Bereit, einen neuen individuellen Fall zu starten.",
        enterEmailBeforeDevBypass:
          "Gib deine E-Mail ein, bevor du die Zahlung in Dev uberspringst.",
        acceptPolicyBeforeDevBypass:
          "Bitte akzeptiere den Hinweis zu Datenschutz und Bestellzugang, bevor du die Zahlung in Dev uberspringst.",
        creatingDevTestOrder: "Dev-Testbestellung wird erstellt...",
        applyingDevPaymentBypass: "Dev-Zahlungsumgehung wird angewendet...",
        unlockingOwnerAccess:
          "Eigentumerzugang fur diesen Browser wird freigeschaltet...",
        devBypassAppliedOpeningIntake:
          "Dev-Bypass angewendet. Intake wird geoffnet...",
        devBypassFailed: "Wir konnten keine Dev-Testbestellung erstellen.",
        unlockingSavedIntake:
          "Dein gespeicherter Intake wird fur diesen Browser freigeschaltet...",
        savedIntakeUnlocked:
          "Gespeicherter Intake entsperrt. Wird jetzt geoffnet...",
        savedIntakeOpenFailed:
          "Wir konnten den gespeicherten Intake nicht offnen.",
        prepareCheckoutFallback:
          "Der Checkout kann im Moment nicht vorbereitet werden.",
        devAccessCodeUnavailable:
          "Die Dev-Vorschau fur den Zugangscode war nicht verfugbar.",
        devBypassFallback:
          "Die Zahlung kann in Dev gerade nicht ubersprungen werden.",
        savedIntakeFallback:
          "Der gespeicherte Intake kann in Dev nicht entsperrt werden.",
      },
    };
  }

  if (lang === "it") {
    return {
      casesSolvedThisMonth: "casi risolti questo mese",
      reviewsLabel: "recensioni",
      stepLabel: "Fase",
      plannerSubmitLabel: "Salva bozza e apri il pagamento",
      policyNoticeBeforeTerms: "Accetto i ",
      termsLabel: "Termini",
      policyNoticeBeforePrivacy: " e la ",
      privacyLabel: "Informativa sulla privacy",
      policyNoticeAfterPrivacy:
        ", e capisco che mi scriverete per accesso all'ordine, consegna e supporto relativo a questo acquisto.",
      devBypassIdle: "Salta pagamento in dev",
      devBypassBusy: "Salto del pagamento in corso...",
      startFreshIntake: "Avvia un nuovo brief",
      runtime: {
        paymentReceivedOpeningIntake:
          "Pagamento ricevuto. Apertura del tuo brief privato...",
        checkoutClosedBeforePayment:
          "Il pagamento e stato chiuso prima della conferma. Riapri il pagamento sicuro quando vuoi.",
        enterEmailBeforeCheckout:
          "Inserisci la tua email prima di avviare il pagamento.",
        acceptPolicyBeforeCheckout:
          "Accetta l'avviso sulla privacy e sull'accesso all'ordine prima di avviare il pagamento.",
        preparingSecureCheckout: "Preparazione del pagamento sicuro...",
        fastspringUnavailable:
          "Il pagamento FastSpring non e disponibile in questo momento.",
        checkoutNotReady: "Il pagamento non e ancora pronto.",
        checkoutOpened:
          "Il pagamento e aperto. Quando termina ti riporteremo automaticamente al tuo brief privato.",
        checkoutStartFailed: "Non siamo riusciti ad avviare il pagamento.",
        freshCaseReady:
          "Tutto pronto per iniziare un nuovo caso personalizzato.",
        enterEmailBeforeDevBypass:
          "Inserisci la tua email prima di saltare il pagamento in dev.",
        acceptPolicyBeforeDevBypass:
          "Accetta l'avviso sulla privacy e sull'accesso all'ordine prima di saltare il pagamento in dev.",
        creatingDevTestOrder: "Creazione ordine di test dev...",
        applyingDevPaymentBypass: "Applicazione bypass pagamento dev...",
        unlockingOwnerAccess:
          "Sblocco accesso proprietario per questo browser...",
        devBypassAppliedOpeningIntake:
          "Bypass dev applicato. Apertura del tuo brief...",
        devBypassFailed: "Non siamo riusciti a creare un ordine di test dev.",
        unlockingSavedIntake: "Sblocco del brief salvato per questo browser...",
        savedIntakeUnlocked: "Brief salvato sbloccato. Apertura in corso...",
        savedIntakeOpenFailed: "Non siamo riusciti ad aprire il brief salvato.",
        prepareCheckoutFallback:
          "Non e possibile preparare il pagamento in questo momento.",
        devAccessCodeUnavailable:
          "Il codice di accesso anteprima dev non era disponibile.",
        devBypassFallback:
          "Non e possibile saltare il pagamento in dev in questo momento.",
        savedIntakeFallback:
          "Non e possibile sbloccare il brief salvato in dev.",
      },
    };
  }

  return {
    casesSolvedThisMonth: "cases solved this month",
    reviewsLabel: "reviews",
    stepLabel: "Step",
    plannerSubmitLabel: "Save Draft and Open Checkout",
    policyNoticeBeforeTerms: "I agree to the ",
    termsLabel: "Terms",
    policyNoticeBeforePrivacy: " and ",
    privacyLabel: "Privacy Policy",
    policyNoticeAfterPrivacy:
      ", and I understand you will email me about order access, delivery, and support for this purchase.",
    devBypassIdle: "Bypass Payment In Dev",
    devBypassBusy: "Bypassing Payment...",
    startFreshIntake: "Start Fresh Intake",
    runtime: {
      paymentReceivedOpeningIntake:
        "Payment received. Opening your private intake...",
      checkoutClosedBeforePayment:
        "Checkout closed before payment was completed. Reopen secure checkout when you're ready.",
      enterEmailBeforeCheckout: "Enter your email before starting checkout.",
      acceptPolicyBeforeCheckout:
        "Review and accept the privacy and order-access notice before starting checkout.",
      preparingSecureCheckout: "Preparing secure checkout...",
      fastspringUnavailable: "FastSpring checkout is not ready right now.",
      checkoutNotReady: "Checkout is not ready yet.",
      checkoutOpened:
        "Checkout opened. When payment finishes, we will return you to your private intake automatically.",
      checkoutStartFailed: "We could not start checkout.",
      freshCaseReady: "Ready to start a fresh custom case.",
      enterEmailBeforeDevBypass:
        "Enter your email before bypassing payment in dev.",
      acceptPolicyBeforeDevBypass:
        "Review and accept the privacy and order-access notice before bypassing payment in dev.",
      creatingDevTestOrder: "Creating a dev test order...",
      applyingDevPaymentBypass: "Applying dev payment bypass...",
      unlockingOwnerAccess: "Unlocking owner access for this browser...",
      devBypassAppliedOpeningIntake:
        "Dev bypass applied. Opening your intake...",
      devBypassFailed: "We could not create a dev test order.",
      unlockingSavedIntake: "Unlocking your saved intake for this browser...",
      savedIntakeUnlocked: "Saved intake unlocked. Opening it now...",
      savedIntakeOpenFailed: "We could not open the saved intake.",
      prepareCheckoutFallback: "Unable to prepare checkout right now.",
      devAccessCodeUnavailable: "Dev access code preview was not available.",
      devBypassFallback: "Unable to bypass payment in dev.",
      savedIntakeFallback: "Unable to unlock the saved intake in dev.",
    },
  };
};

const getHeroTitleClassName = (lang: keyof typeof CUSTOM_ENTRY_COPY) => {
  if (lang === "en") {
    return MARKETING_HERO_TITLE_CLASS;
  }

  if (lang === "de") {
    return "text-[2.55rem] font-semibold leading-[0.98] sm:text-[4.35rem] lg:text-[5.15rem] xl:text-[5.35rem]";
  }

  return "text-[2.5rem] font-semibold leading-[0.98] sm:text-[4.2rem] lg:text-[4.95rem] xl:text-[5.15rem]";
};

const getHeroBodyMaxWidthClassName = (lang: keyof typeof CUSTOM_ENTRY_COPY) =>
  lang === "en" ? "max-w-4xl" : "max-w-[46rem]";

const getCustomCasePriceDisplay = (lang: keyof typeof CUSTOM_ENTRY_COPY) => {
  if (lang === "en") {
    return {
      whole: "$39",
      fraction: ".95",
      inline: "$39.95 USD",
      currency: "USD",
    };
  }

  return {
    whole: "39",
    fraction: ",95",
    inline: "39,95 USD",
    currency: "USD",
  };
};

const CustomStoriesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CustomCaseFormData>(() =>
    loadStoredCustomCaseFormData(),
  );
  const [errors, setErrors] = useState<CustomCaseFormErrors>({});
  const [isLaunchingCheckout, setIsLaunchingCheckout] = useState(false);
  const [isBypassingPayment, setIsBypassingPayment] = useState(false);
  const [resumeAccessToken, setResumeAccessToken] = useState(() =>
    getLastCustomOrderAccessToken(),
  );
  const [checkoutStatus, setCheckoutStatus] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const [policyNoticeNeedsAttention, setPolicyNoticeNeedsAttention] =
    useState(false);
  const [hasTrackedEmailEntry, setHasTrackedEmailEntry] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [showPlanner, setShowPlanner] = useState(
    () => loadStoredCustomCaseFormData().suspects.length > 0,
  );
  const showDevPaymentBypass = Boolean((import.meta as any).env?.DEV);
  const isComingSoon = CUSTOM_CASE_COMING_SOON_ENABLED;
  const landingContext = useMemo(
    () => normalizeCustomLandingContext(new URLSearchParams(location.search)),
    [location.search],
  );
  const landingTopicLabel = useMemo(
    () => getCustomLandingTopicLabel(landingContext.topic, landingContext.lang),
    [landingContext.lang, landingContext.topic],
  );
  const entryCopy = CUSTOM_ENTRY_COPY[landingContext.lang];
  const localizedPageContent = useMemo(
    () => buildLocalizedPageContent(landingContext.lang),
    [landingContext.lang],
  );
  const formCopy = useMemo(
    () => buildLocalizedFormCopy(landingContext.lang),
    [landingContext.lang],
  );
  const uiCopy = useMemo(
    () => buildLocalizedUiCopy(landingContext.lang),
    [landingContext.lang],
  );
  const resolveApiErrorMessage = (error: any, fallback: string) =>
    getLocalizedCustomCaseApiMessage({
      error,
      locale: landingContext.lang,
      fallback,
    });
  const heroTitleClassName = useMemo(
    () => getHeroTitleClassName(landingContext.lang),
    [landingContext.lang],
  );
  const heroBodyMaxWidthClassName = useMemo(
    () => getHeroBodyMaxWidthClassName(landingContext.lang),
    [landingContext.lang],
  );
  const customCasePriceDisplay = useMemo(
    () => getCustomCasePriceDisplay(landingContext.lang),
    [landingContext.lang],
  );
  const localizedErrors = useMemo(
    () => localizeFormErrors(errors, landingContext.lang),
    [errors, landingContext.lang],
  );
  const buildIntakePath = (accessToken: string) =>
    buildCustomLandingPath(
      `/custom/intake/${encodeURIComponent(accessToken)}`,
      landingContext,
    );

  const hasPlannerData = useMemo(
    () =>
      Boolean(formData.city || formData.location || formData.source) ||
      formData.suspects.length > 0,
    [formData],
  );
  const activeBuilderCount = useMemo(
    () => Math.floor(Math.random() * (32 - 14 + 1)) + 14,
    [],
  );
  const casesThisMonthCount = useMemo(
    () => getCasesThisMonthCount(new Date()),
    [],
  );
  const summaryStats = useMemo(
    () =>
      SUMMARY_STATS.map((stat) =>
        stat.label === "Cases created this month"
          ? {
              ...stat,
              value: String(casesThisMonthCount),
              label: localizedPageContent.summaryLabels[0],
            }
          : stat.label === "Average rating"
            ? { ...stat, label: localizedPageContent.summaryLabels[1] }
            : stat.label === "Average build time"
              ? { ...stat, label: localizedPageContent.summaryLabels[2] }
              : { ...stat, label: localizedPageContent.summaryLabels[3] },
      ),
    [casesThisMonthCount, localizedPageContent.summaryLabels],
  );

  useEffect(() => {
    devLog("route", "custom_case_sales_route_rendered", {
      route: "/custom",
      hasStoredDraft: hasPlannerData,
      hasResumeAccessToken: Boolean(resumeAccessToken),
      comingSoonEnabled: isComingSoon,
      lang: landingContext.lang,
      topic: landingContext.topic || null,
      attributionSource: landingContext.source || null,
      utmCampaign: landingContext.utmCampaign || null,
    });
    track("View Custom Story Creator", {
      timestamp: new Date().toISOString(),
      mode: "purchase_first",
      lang: landingContext.lang,
      topic: landingContext.topic || undefined,
    });
  }, [
    hasPlannerData,
    isComingSoon,
    landingContext.lang,
    landingContext.source,
    landingContext.topic,
    landingContext.utmCampaign,
    resumeAccessToken,
  ]);

  useEffect(() => {
    devLog("route", "custom_case_landing_context_normalized", {
      route: "/custom",
      lang: landingContext.lang,
      topic: landingContext.topic || null,
      attributionSource: landingContext.source || null,
      utmSource: landingContext.utmSource || null,
      utmMedium: landingContext.utmMedium || null,
      utmCampaign: landingContext.utmCampaign || null,
      hadLangFallback: landingContext.hadLangFallback,
      hadTopicFallback: landingContext.hadTopicFallback,
    });
  }, [
    landingContext.hadLangFallback,
    landingContext.hadTopicFallback,
    landingContext.lang,
    landingContext.source,
    landingContext.topic,
    landingContext.utmCampaign,
    landingContext.utmMedium,
    landingContext.utmSource,
  ]);

  useEffect(() => {
    devLog("route", "custom_case_primary_cta_seen", {
      route: "/custom",
      source: "hero_checkout_card",
    });
    track("Custom Case Primary CTA Seen", {
      timestamp: new Date().toISOString(),
      source: "hero_checkout_card",
    });
  }, []);

  useEffect(() => {
    if (!formData.email) {
      const savedEmail =
        getLastCustomOrderEmail() || cartService.getCustomerEmail();
      if (savedEmail) {
        setFormData((current) => ({ ...current, email: savedEmail }));
      }
    }
  }, [formData.email]);

  useEffect(() => {
    storeCustomCaseFormData(formData);
  }, [formData]);

  const applyFormDataUpdate = (
    updater: (current: CustomCaseFormData) => CustomCaseFormData,
  ) => {
    setFormData((current) => updater(current));
  };

  const handleAddSuspect = () => {
    if (formData.suspects.length >= 6) return;
    applyFormDataUpdate((current) => ({
      ...current,
      suspects: [...current.suspects, getEmptySuspect()],
    }));
    track("Add Custom Story Suspect", {
      total_suspects: formData.suspects.length + 1,
      timestamp: new Date().toISOString(),
    });
  };

  const handleRemoveSuspect = (index: number) => {
    applyFormDataUpdate((current) => ({
      ...current,
      suspects: current.suspects.filter(
        (_, suspectIndex) => suspectIndex !== index,
      ),
    }));
    track("Remove Custom Story Suspect", {
      suspect_index: index,
      total_suspects: Math.max(0, formData.suspects.length - 1),
      timestamp: new Date().toISOString(),
    });
  };

  const handleSuspectChange = (
    index: number,
    field: keyof (typeof formData.suspects)[number],
    value: string,
  ) => {
    applyFormDataUpdate((current) => ({
      ...current,
      suspects: current.suspects.map((suspect, suspectIndex) =>
        suspectIndex === index ? { ...suspect, [field]: value } : suspect,
      ),
    }));
  };

  const handlePlannerToggle = () => {
    const nextValue = !showPlanner;
    setShowPlanner(nextValue);
    devLog("route", "custom_case_planner_toggled", {
      route: "/custom",
      visible: nextValue,
    });
    track("Custom Case Planner Toggled", {
      visible: nextValue,
      timestamp: new Date().toISOString(),
    });
  };

  useEffect(() => {
    if (!resumeAccessToken) return;

    let cancelled = false;
    devLog("route", "custom_case_resume_token_validation_started", {
      route: "/custom",
      hasResumeAccessToken: true,
    });

    void CustomOrderAPI.getAccessState(resumeAccessToken)
      .then((response) => {
        if (cancelled) return;
        if (response?.access?.paymentConfirmed) {
          devLog("route", "custom_case_resume_token_validation_succeeded", {
            route: "/custom",
            hasResumeAccessToken: true,
            status: String(response?.access?.status || ""),
          });
          return;
        }

        clearLastCustomOrderAccessToken();
        setResumeAccessToken("");
        devWarn("route", "custom_case_resume_token_cleared_unpaid", {
          route: "/custom",
          status: String(response?.access?.status || ""),
        });
      })
      .catch((error: any) => {
        if (cancelled) return;
        clearLastCustomOrderAccessToken();
        setResumeAccessToken("");
        devError("route", "custom_case_resume_token_validation_failed", {
          route: "/custom",
          status: error?.response?.status,
          message:
            error?.response?.data?.message ||
            error?.message ||
            "resume_token_validation_failed",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [resumeAccessToken]);

  useEffect(() => {
    const handleFastSpringClose = (event: Event) => {
      const detail = ((event as CustomEvent<FastSpringPopupClosePayload>)
        .detail || null) as FastSpringPopupClosePayload;
      const pendingAccessToken = getPendingCustomOrderAccessToken();
      if (!pendingAccessToken) return;

      if (isFastSpringPopupPurchaseComplete(detail)) {
        setCheckoutError("");
        setCheckoutStatus(uiCopy.runtime.paymentReceivedOpeningIntake);
        devLog("route", "custom_case_checkout_popup_completed", {
          route: "/custom",
          accessToken: pendingAccessToken,
          fastspringOrderId: String(detail?.id || ""),
          fastspringOrderReference: String(detail?.reference || ""),
        });
        navigate(
          buildCustomLandingPath(
            `/custom/intake/${encodeURIComponent(pendingAccessToken)}`,
            landingContext,
          ),
        );
        return;
      }

      clearPendingCustomOrderAccessToken();
      setCheckoutStatus(uiCopy.runtime.checkoutClosedBeforePayment);
      devLog("route", "custom_case_checkout_cancelled_before_payment", {
        route: "/custom",
        accessToken: pendingAccessToken,
      });
    };

    window.addEventListener(
      "fastspring:close",
      handleFastSpringClose as EventListener,
    );
    return () => {
      window.removeEventListener(
        "fastspring:close",
        handleFastSpringClose as EventListener,
      );
    };
  }, [
    landingContext,
    navigate,
    uiCopy.runtime.checkoutClosedBeforePayment,
    uiCopy.runtime.paymentReceivedOpeningIntake,
  ]);

  const handleCheckoutEmailChange = (value: string) => {
    setFormData((current) => ({ ...current, email: value }));
    if (!hasTrackedEmailEntry && value.trim()) {
      setHasTrackedEmailEntry(true);
      devLog("route", "custom_case_checkout_email_started", {
        route: "/custom",
      });
      track("Custom Case Checkout Email Started", {
        timestamp: new Date().toISOString(),
      });
    }
  };

  const bootstrapOrder = async (source: string) => {
    devLog("api", "custom_case_checkout_bootstrap_started", {
      route: "/custom",
      source,
      hasResumeAccessToken: Boolean(resumeAccessToken),
      hasEmail: Boolean(formData.email),
      productPath: CUSTOM_CASE_PRODUCT_PATH,
    });
    const response = await CustomOrderAPI.bootstrap({
      email: formData.email,
      packageKey: "custom-case",
      source,
      language: landingContext.lang,
      topic: landingContext.topic || undefined,
      attributionSource: landingContext.source || undefined,
      utmSource: landingContext.utmSource || undefined,
      utmMedium: landingContext.utmMedium || undefined,
      utmCampaign: landingContext.utmCampaign || undefined,
      forceNew: true,
      marketingOptIn: formData.marketingOptIn,
      marketingOptInSource: "custom_checkout_card",
      policyAccepted: formData.policyAccepted,
    });

    const order = response.order;
    savePendingCustomOrderAccessToken(order.accessToken);
    saveLastCustomOrderEmail(order.customerEmail || formData.email);
    cartService.saveCustomerEmail(order.customerEmail || formData.email);

    devLog("api", "custom_case_checkout_bootstrap_succeeded", {
      route: "/custom",
      source,
      orderId: order.id,
      orderStatus: order.status,
      accessToken: order.accessToken,
      paymentConfirmed: Boolean(order.paymentConfirmedAt),
    });

    return order;
  };

  const launchCheckoutForCurrentEmail = async (
    source: string,
    options: { saveDraftFirst?: boolean } = {},
  ) => {
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      setErrors((current) => ({
        ...current,
        email: "Please enter a valid email",
      }));
      setCheckoutError(uiCopy.runtime.enterEmailBeforeCheckout);
      devWarn("route", "custom_case_checkout_blocked_missing_email", {
        route: "/custom",
        source,
      });
      return;
    }

    if (!formData.policyAccepted) {
      setPolicyNoticeNeedsAttention(true);
      setCheckoutError(uiCopy.runtime.acceptPolicyBeforeCheckout);
      devWarn(
        "route",
        "custom_case_checkout_blocked_missing_policy_acceptance",
        {
          route: "/custom",
          source,
        },
      );
      return;
    }

    try {
      setIsLaunchingCheckout(true);
      setCheckoutError("");
      setCheckoutStatus(uiCopy.runtime.preparingSecureCheckout);
      const order = await bootstrapOrder(source);

      if (options.saveDraftFirst) {
        await CustomOrderAPI.saveIntakeDraft(
          order.accessToken,
          formData as unknown as Record<string, unknown>,
        );
        devLog("api", "custom_case_checkout_draft_saved_before_launch", {
          route: "/custom",
          source,
          accessToken: order.accessToken,
          suspectCount: formData.suspects.length,
        });
      }

      devLog("route", "custom_case_checkout_launch_requested", {
        route: "/custom",
        source,
        orderId: order.id,
        accessToken: order.accessToken,
        saveDraftFirst: Boolean(options.saveDraftFirst),
      });
      const checkout = launchCustomCheckout({
        email: formData.email,
        customOrderId: order.id,
        accessToken: order.accessToken,
        landingContext,
      });

      if (!checkout.launched) {
        clearPendingCustomOrderAccessToken();
        setCheckoutError(uiCopy.runtime.fastspringUnavailable);
        setCheckoutStatus(uiCopy.runtime.checkoutNotReady);
        devWarn("route", "custom_case_checkout_launch_unavailable", {
          route: "/custom",
          source,
          accessToken: order.accessToken,
        });
        return;
      }

      setCheckoutStatus(uiCopy.runtime.checkoutOpened);
      devLog("route", "custom_case_checkout_launch_succeeded", {
        route: "/custom",
        source,
        orderId: order.id,
        accessToken: order.accessToken,
        method: checkout.method,
        correlation: checkout.correlation,
      });
      track("Custom Story Checkout Started", {
        source,
        has_draft: Boolean(options.saveDraftFirst),
        timestamp: new Date().toISOString(),
      });
    } catch (error: any) {
      clearPendingCustomOrderAccessToken();
      const nextMessage = resolveApiErrorMessage(
        error,
        uiCopy.runtime.prepareCheckoutFallback,
      );
      setCheckoutError(nextMessage);
      setCheckoutStatus(uiCopy.runtime.checkoutStartFailed);
      devError("api", "custom_case_checkout_bootstrap_failed", {
        route: "/custom",
        source,
        message: nextMessage,
        status: error?.response?.status,
      });
    } finally {
      setIsLaunchingCheckout(false);
    }
  };

  const handleDirectCheckout = async (source = "purchase_first_cta") => {
    devLog("route", "custom_case_primary_cta_clicked", {
      route: "/custom",
      source,
      hasEmail: Boolean(formData.email),
      hasPlannerData,
    });
    track("Custom Case Primary CTA Clicked", {
      source,
      has_email: Boolean(formData.email),
      timestamp: new Date().toISOString(),
    });
    await launchCheckoutForCurrentEmail(source);
  };

  const handlePlannerSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    const nextErrors = validateCustomCaseForm(formData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      devWarn("route", "custom_case_planner_validation_failed", {
        route: "/custom",
        suspectErrorCount: Array.isArray(nextErrors.suspects)
          ? nextErrors.suspects.length
          : 0,
      });
      return;
    }
    await launchCheckoutForCurrentEmail("planner_checkout_confirmed", {
      saveDraftFirst: true,
    });
  };

  const handleStartFreshOrder = () => {
    clearPendingCustomOrderAccessToken();
    clearLastCustomOrderAccessToken();
    clearLastCustomOrderEmail();
    clearStoredCustomCaseFormData();
    setResumeAccessToken("");
    setCheckoutStatus(uiCopy.runtime.freshCaseReady);
    setCheckoutError("");
    devLog("route", "custom_case_start_fresh_requested", {
      route: "/custom",
      hadResumeAccessToken: Boolean(resumeAccessToken),
    });
    track("Custom Case Start Fresh Requested", {
      timestamp: new Date().toISOString(),
      had_resume_access_token: Boolean(resumeAccessToken),
    });
  };

  const verifyOwnerAccessInDev = async (
    accessToken: string,
    source: string,
  ) => {
    devLog("route", "custom_case_dev_owner_access_started", {
      route: "/custom",
      source,
      hasAccessToken: Boolean(accessToken),
    });
    const codeResponse = await CustomOrderAPI.requestAccessCode({
      accessToken,
    });
    const devCode = String(codeResponse?.access?.devCodePreview || "").trim();
    if (!/^\d{6}$/.test(devCode)) {
      throw new Error(uiCopy.runtime.devAccessCodeUnavailable);
    }
    const verification = await CustomOrderAPI.verifyAccessCode({
      accessToken,
      code: devCode,
    });
    storeCustomOrderOwnerSessionToken(
      String(verification?.access?.accessToken || accessToken || "").trim(),
      String(verification?.access?.sessionToken || "").trim(),
    );
    devLog("route", "custom_case_dev_owner_access_succeeded", {
      route: "/custom",
      source,
      hasAccessToken: Boolean(accessToken),
      hasSessionToken: Boolean(
        String(verification?.access?.sessionToken || "").trim(),
      ),
    });
  };

  const handleDevPaymentBypass = async (
    source = "hero_checkout_card_dev_bypass",
  ) => {
    if (!showDevPaymentBypass) return;

    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      setErrors((current) => ({
        ...current,
        email: "Please enter a valid email",
      }));
      setCheckoutError(uiCopy.runtime.enterEmailBeforeDevBypass);
      devWarn("route", "custom_case_dev_payment_bypass_blocked_missing_email", {
        route: "/custom",
        source,
      });
      return;
    }

    if (!formData.policyAccepted) {
      setCheckoutError(uiCopy.runtime.acceptPolicyBeforeDevBypass);
      devWarn(
        "route",
        "custom_case_dev_payment_bypass_blocked_missing_policy_acceptance",
        {
          route: "/custom",
          source,
        },
      );
      return;
    }

    try {
      setIsBypassingPayment(true);
      setCheckoutError("");
      setCheckoutStatus(uiCopy.runtime.creatingDevTestOrder);
      devLog("route", "custom_case_dev_payment_bypass_started", {
        route: "/custom",
        source,
        hasEmail: Boolean(formData.email),
      });
      const order = await bootstrapOrder(source);
      setCheckoutStatus(uiCopy.runtime.applyingDevPaymentBypass);
      const response = await CustomOrderAPI.confirmDevPayment(
        order.accessToken,
      );
      clearPendingCustomOrderAccessToken();
      setCheckoutStatus(uiCopy.runtime.unlockingOwnerAccess);
      await verifyOwnerAccessInDev(
        response.order.accessToken,
        `${source}_owner_access`,
      );
      setCheckoutStatus(uiCopy.runtime.devBypassAppliedOpeningIntake);
      devLog("route", "custom_case_dev_payment_bypass_succeeded", {
        route: "/custom",
        source,
        accessToken: response.order.accessToken,
        status: response.order.status,
        paymentConfirmed: Boolean(response.order.paymentConfirmedAt),
      });
      navigate(buildIntakePath(response.order.accessToken));
    } catch (error: any) {
      const nextMessage = resolveApiErrorMessage(
        error,
        uiCopy.runtime.devBypassFallback,
      );
      setCheckoutError(nextMessage);
      setCheckoutStatus(uiCopy.runtime.devBypassFailed);
      devError("route", "custom_case_dev_payment_bypass_failed", {
        route: "/custom",
        source,
        status: error?.response?.status,
        message: nextMessage,
      });
    } finally {
      setIsBypassingPayment(false);
    }
  };

  const handleContinuePreviousIntakeInDev = async () => {
    if (!showDevPaymentBypass || !resumeAccessToken) {
      if (resumeAccessToken) {
        navigate(buildIntakePath(resumeAccessToken));
      }
      return;
    }

    try {
      setIsBypassingPayment(true);
      setCheckoutError("");
      setCheckoutStatus(uiCopy.runtime.unlockingSavedIntake);
      try {
        await verifyOwnerAccessInDev(
          resumeAccessToken,
          "resume_previous_intake_dev_bypass",
        );
      } catch (error: any) {
        if (String(error?.response?.data?.code || "") === "payment_required") {
          await CustomOrderAPI.confirmDevPayment(resumeAccessToken);
          await verifyOwnerAccessInDev(
            resumeAccessToken,
            "resume_previous_intake_dev_confirmed",
          );
        } else {
          throw error;
        }
      }
      setCheckoutStatus(uiCopy.runtime.savedIntakeUnlocked);
      devLog("route", "custom_case_dev_resume_intake_succeeded", {
        route: "/custom",
        accessToken: resumeAccessToken,
      });
      navigate(buildIntakePath(resumeAccessToken));
    } catch (error: any) {
      const nextMessage = resolveApiErrorMessage(
        error,
        uiCopy.runtime.savedIntakeFallback,
      );
      setCheckoutError(nextMessage);
      setCheckoutStatus(uiCopy.runtime.savedIntakeOpenFailed);
      devError("route", "custom_case_dev_resume_intake_failed", {
        route: "/custom",
        accessToken: resumeAccessToken,
        status: error?.response?.status,
        message: nextMessage,
      });
    } finally {
      setIsBypassingPayment(false);
    }
  };

  const handleStartFreshIntakeInDev = async () => {
    if (!showDevPaymentBypass) {
      handleStartFreshOrder();
      return;
    }

    handleStartFreshOrder();
    await handleDevPaymentBypass("resume_card_start_fresh_dev_bypass");
  };

  return (
    <>
      <Helmet>
        <title>
          {landingTopicLabel
            ? `${entryCopy.startButton} | ${landingTopicLabel} | Cold Case Game Kits`
            : "Custom Cold Case | Cold Case Game Kits"}
        </title>
        <meta
          name="description"
          content={
            isComingSoon
              ? entryCopy.comingSoonDescription
              : entryCopy.heroDescription
          }
        />
      </Helmet>

      <div className="relative min-h-screen overflow-hidden bg-[#07060a] text-[#fff7ef]">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-[-10rem] top-[-14rem] h-[34rem] w-[34rem] rounded-full bg-[radial-gradient(circle,_rgba(171,36,61,0.26)_0%,_rgba(171,36,61,0)_68%)]" />
          <div className="absolute right-[-12rem] top-[10rem] h-[34rem] w-[34rem] rounded-full bg-[radial-gradient(circle,_rgba(171,36,61,0.14)_0%,_rgba(171,36,61,0)_72%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(7,6,10,0.2)_0%,rgba(7,6,10,0.84)_45%,#07060a_100%)]" />
        </div>

        <div className="relative mx-auto max-w-[1400px] px-4 py-7 sm:px-6 sm:py-10 lg:px-8 lg:py-14">
          <section className="grid gap-12 xl:grid-cols-[minmax(0,1.08fr)_500px] 2xl:grid-cols-[minmax(0,1.12fr)_520px] xl:items-start">
            <div className="space-y-8 pt-2 lg:pt-6">
              <div className="flex justify-center xl:justify-start">
                <div className="inline-flex items-center gap-3 rounded-full border border-[#5b1c2b] bg-[#19080f] px-5 py-3 text-sm text-[#f0cbd5] shadow-[0_18px_60px_rgba(49,7,18,0.24)]">
                  <Star className="h-4 w-4 fill-[#b3314d] text-[#b3314d]" />
                  {casesThisMonthCount}+ {uiCopy.casesSolvedThisMonth}
                </div>
              </div>

              <div className="max-w-[48rem] space-y-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-[#4c1c26] bg-[#14090d] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#d8bcc3]">
                  <Fingerprint className="h-3.5 w-3.5 text-[#b3314d]" />
                  {isComingSoon
                    ? entryCopy.comingSoonEyebrow
                    : entryCopy.eyebrow}
                </div>
                <h1
                  className={`max-w-[10.5ch] font-heading text-[#fff8f3] ${heroTitleClassName}`}
                  style={{ textWrap: "balance" }}
                >
                  {isComingSoon ? (
                    <>{entryCopy.comingSoonTitle}</>
                  ) : (
                    entryCopy.heroTitle
                  )}
                </h1>
                <p
                  className={`${heroBodyMaxWidthClassName} text-[#aaa1a6] ${MARKETING_BODY_READING_CLASS}`}
                  style={{ textWrap: "pretty" }}
                >
                  {isComingSoon
                    ? entryCopy.comingSoonDescription
                    : entryCopy.heroDescription}
                </p>
              </div>

              <div className="flex flex-wrap gap-x-8 gap-y-4 text-lg text-[#9f949a]">
                {TRUST_BADGES.map(({ icon: Icon }, index) => (
                  <div
                    key={localizedPageContent.trustBadges[index]}
                    className="inline-flex items-center gap-3"
                  >
                    <Icon className="h-5 w-5 text-[#b3314d]" />
                    <span>{localizedPageContent.trustBadges[index]}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-5 text-[#ddd6da]">
                <div>
                  <div className="flex items-center gap-1 text-[#b3314d]">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Star key={index} className="h-5 w-5 fill-current" />
                    ))}
                  </div>
                  <p className="mt-1 text-xl font-semibold text-[#fff8f3] sm:text-2xl">
                    4.9/5{" "}
                    <span className="text-base font-normal text-[#988d93]">
                      {uiCopy.reviewsLabel}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <aside className="xl:sticky xl:top-24 xl:self-start">
              <div className="overflow-hidden rounded-[2rem] border border-[#341b23] bg-[linear-gradient(180deg,#0f090c_0%,#12080d_100%)] text-[#fff8f3] shadow-[0_32px_120px_rgba(0,0,0,0.46)] sm:rounded-[2.3rem]">
                <div className="border-b border-white/8 px-5 py-6 sm:px-7 sm:py-7">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-[#a39299]">
                        {entryCopy.startCaseLabel}
                      </p>
                      <div className="mt-3 flex items-end gap-2">
                        <p className="font-heading text-[3.4rem] font-semibold leading-none sm:text-6xl">
                          {customCasePriceDisplay.whole}
                        </p>
                        <span className="pb-1 text-2xl text-[#a39299] sm:text-3xl">
                          {customCasePriceDisplay.fraction}
                        </span>
                        <span className="pb-1 text-sm font-semibold uppercase tracking-[0.18em] text-[#d25b77] sm:text-base">
                          {customCasePriceDisplay.currency}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex rounded-full bg-[#2a0d16] px-4 py-2 text-sm font-semibold text-[#d25b77]">
                        {localizedPageContent.oneTime}
                      </span>
                      <p className="mt-3 text-sm text-[#908389]">
                        {localizedPageContent.noSubscription}
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 space-y-4 text-lg text-[#cec4c9]">
                    {localizedPageContent.panelBenefits.map((benefit) => (
                      <div key={benefit} className="flex items-center gap-4">
                        <BadgeCheck className="h-5 w-5 text-[#b3314d]" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-5 px-5 py-6 sm:px-7 sm:py-7">
                  <div>
                    <label
                      htmlFor="purchase-email"
                      className="block text-sm text-[#9d9098]"
                    >
                      {entryCopy.emailLabel}
                    </label>
                    <input
                      id="purchase-email"
                      type="email"
                      value={formData.email}
                      onChange={(event) =>
                        handleCheckoutEmailChange(event.target.value)
                      }
                      className="mt-4 w-full rounded-[1.1rem] border border-[#341b23] bg-[#13080d] px-5 py-4 text-lg text-[#fff8f3] outline-none transition placeholder:text-[#72656c] focus:border-[#b3314d] focus:ring-2 focus:ring-[#b3314d]/15"
                      placeholder={
                        formCopy?.emailPlaceholder || "your@email.com"
                      }
                    />
                    {localizedErrors.email ? (
                      <p className="mt-2 text-sm text-[#d76880]">
                        {localizedErrors.email}
                      </p>
                    ) : null}
                  </div>

                  <div className="space-y-3">
                    <label
                      className={`flex items-start gap-3 rounded-[1.2rem] border px-4 py-4 text-sm leading-6 transition ${
                        policyNoticeNeedsAttention
                          ? "border-[#b3314d] bg-[#2a0d16] text-[#f5d7df] shadow-[0_0_0_1px_rgba(179,49,77,0.22)]"
                          : "border-[#341b23] bg-[#15090f] text-[#b8abb1]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.policyAccepted}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setFormData((current) => ({
                            ...current,
                            policyAccepted: checked,
                          }));
                          if (checked) {
                            setPolicyNoticeNeedsAttention(false);
                            setCheckoutError((current) =>
                              current ===
                              uiCopy.runtime.acceptPolicyBeforeCheckout
                                ? ""
                                : current,
                            );
                          }
                        }}
                        className={`mt-1 h-4 w-4 rounded text-[#b3314d] focus:ring-[#b3314d] ${
                          policyNoticeNeedsAttention
                            ? "border-[#d76880] bg-[#2a0d16] ring-2 ring-[#b3314d]/25"
                            : "border-[#4a3139] bg-[#15090f]"
                        }`}
                      />
                      <span>
                        {uiCopy.policyNoticeBeforeTerms}
                        <a
                          href="/terms"
                          className="font-semibold text-[#f0dde3] underline"
                        >
                          {uiCopy.termsLabel}
                        </a>{" "}
                        {uiCopy.policyNoticeBeforePrivacy}
                        <a
                          href="/privacy"
                          className="font-semibold text-[#f0dde3] underline"
                        >
                          {uiCopy.privacyLabel}
                        </a>
                        {uiCopy.policyNoticeAfterPrivacy}
                      </span>
                    </label>
                    {policyNoticeNeedsAttention ? (
                      <p className="text-sm text-[#d76880]">
                        {uiCopy.runtime.acceptPolicyBeforeCheckout}
                      </p>
                    ) : null}
                    <label className="flex items-start gap-3 rounded-[1.2rem] border border-[#341b23] bg-[#15090f] px-4 py-4 text-sm leading-6 text-[#b8abb1]">
                      <input
                        type="checkbox"
                        checked={formData.marketingOptIn}
                        onChange={(event) =>
                          setFormData((current) => ({
                            ...current,
                            marketingOptIn: event.target.checked,
                          }))
                        }
                        className="mt-1 h-4 w-4 rounded border-[#4a3139] bg-[#15090f] text-[#b3314d] focus:ring-[#b3314d]"
                      />
                      <span>{entryCopy.marketingOptIn}</span>
                    </label>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleDirectCheckout("hero_checkout_card")}
                    disabled={isLaunchingCheckout || isBypassingPayment}
                    className={`inline-flex w-full items-center justify-center gap-3 rounded-[1rem] bg-[#b3314d] px-6 py-4 sm:py-5 text-white shadow-[0_18px_50px_rgba(179,49,77,0.28)] transition hover:bg-[#c63a59] disabled:cursor-not-allowed disabled:opacity-50 ${MARKETING_CTA_TEXT_CLASS}`}
                  >
                    {isLaunchingCheckout
                      ? entryCopy.preparingButton
                      : entryCopy.startButton}
                    <ArrowRight className="h-6 w-6" />
                  </button>

                  {showDevPaymentBypass ? (
                    <button
                      type="button"
                      onClick={() =>
                        handleDevPaymentBypass("hero_checkout_card_dev_bypass")
                      }
                      disabled={isLaunchingCheckout || isBypassingPayment}
                      className="inline-flex w-full items-center justify-center gap-3 rounded-[1rem] border border-[#4f2230] bg-[#14090d] px-6 py-4 text-lg font-semibold text-[#f0dde3] transition hover:border-[#b3314d] hover:bg-[#1a0b11] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isBypassingPayment
                        ? uiCopy.devBypassBusy
                        : uiCopy.devBypassIdle}
                    </button>
                  ) : null}

                  <p className="text-center text-base leading-7 text-[#908389]">
                    {entryCopy.payFirstNote}
                  </p>

                  <div className="rounded-[1.3rem] border border-[#4a1f2b] bg-[#18090f] px-5 py-4 text-center text-lg text-[#d4627d]">
                    {activeBuilderCount} {localizedPageContent.buildersSuffix}
                  </div>

                  <div className="rounded-[1.5rem] border border-[#342028] bg-[#130b10] p-5 text-[#f6ede6] sm:rounded-[1.7rem]">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="h-5 w-5 text-[#b3314d]" />
                      <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[#f4e4c3]">
                        {localizedPageContent.panelHowItWorks}
                      </p>
                    </div>
                    <div className="mt-4 space-y-3">
                      {localizedPageContent.deliverySteps.map((step, index) => (
                        <div key={step} className="flex gap-3">
                          <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#b3314d]/16 text-[11px] font-semibold text-[#f4e4c3]">
                            {index + 1}
                          </div>
                          <p className="text-sm leading-6 text-[#ead9cf]">
                            {step}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {resumeAccessToken ? (
                    <div className="rounded-[1.5rem] border border-emerald-500/25 bg-emerald-100 p-4 text-emerald-950 sm:rounded-[1.7rem]">
                      <p className="text-sm font-semibold text-emerald-900">
                        {entryCopy.resumeTitle}
                      </p>
                      <p className="mt-2 text-sm leading-6 text-emerald-950/75">
                        {entryCopy.resumeBody}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-3">
                        {showDevPaymentBypass ? (
                          <button
                            type="button"
                            onClick={() =>
                              void handleContinuePreviousIntakeInDev()
                            }
                            disabled={isBypassingPayment || isLaunchingCheckout}
                            className="inline-flex rounded-full bg-emerald-400 px-4 py-2 text-sm font-semibold text-black hover:bg-emerald-300 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {entryCopy.continueResume}
                          </button>
                        ) : (
                          <a
                            href={buildIntakePath(resumeAccessToken)}
                            className="inline-flex rounded-full bg-emerald-400 px-4 py-2 text-sm font-semibold text-black hover:bg-emerald-300"
                          >
                            {entryCopy.continueResume}
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            void (showDevPaymentBypass
                              ? handleStartFreshIntakeInDev()
                              : Promise.resolve(handleStartFreshOrder()))
                          }
                          disabled={isBypassingPayment || isLaunchingCheckout}
                          className="inline-flex rounded-full border border-emerald-900/15 bg-white/70 px-4 py-2 text-sm font-semibold text-emerald-950 hover:bg-white"
                        >
                          {showDevPaymentBypass
                            ? uiCopy.startFreshIntake
                            : entryCopy.startFresh}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {checkoutStatus ? (
                    <div className="rounded-[1.5rem] border border-emerald-500/25 bg-emerald-100 p-4 sm:rounded-[1.7rem]">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-900/75">
                        {entryCopy.checkoutStatusLabel}
                      </p>
                      <p className="mt-2 text-sm leading-6 text-emerald-950/80">
                        {checkoutStatus}
                      </p>
                      {resumeAccessToken ? (
                        showDevPaymentBypass ? (
                          <button
                            type="button"
                            onClick={() =>
                              void handleContinuePreviousIntakeInDev()
                            }
                            disabled={isBypassingPayment || isLaunchingCheckout}
                            className="mt-4 inline-flex rounded-full border border-emerald-900/12 bg-white/70 px-4 py-2 text-sm font-semibold text-emerald-950 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {entryCopy.continueResume}
                          </button>
                        ) : (
                          <a
                            href={buildIntakePath(resumeAccessToken)}
                            className="mt-4 inline-flex rounded-full border border-emerald-900/12 bg-white/70 px-4 py-2 text-sm font-semibold text-emerald-950 hover:bg-white"
                          >
                            {entryCopy.continueResume}
                          </a>
                        )
                      ) : null}
                    </div>
                  ) : null}

                  {checkoutError ? (
                    <div className="rounded-[1.5rem] border border-red-500/30 bg-red-100 p-4 text-sm text-[#892138] sm:rounded-[1.7rem]">
                      {checkoutError}
                    </div>
                  ) : null}
                </div>
              </div>
            </aside>
          </section>

          <section className="mt-24">
            <div className="mx-auto max-w-5xl text-center">
              <p className="text-[13px] uppercase tracking-[0.24em] text-[#b3314d]">
                {localizedPageContent.howItWorksEyebrow}
              </p>
              <h2
                className={`font-heading text-[#fff8f3] ${MARKETING_SECTION_TITLE_CLASS}`}
              >
                {localizedPageContent.howItWorksTitle}
              </h2>
            </div>

            <div className="mt-14 grid gap-5 lg:grid-cols-4">
              {HOW_IT_WORKS.map(({ icon: Icon }, index) => (
                <article
                  key={localizedPageContent.howItWorks[index].title}
                  className="relative rounded-[2rem] border border-[#2f2026] bg-[#0f090c] p-6 shadow-[0_16px_48px_rgba(0,0,0,0.2)] sm:p-8"
                >
                  {index < HOW_IT_WORKS.length - 1 ? (
                    <span className="pointer-events-none absolute right-[-16px] top-1/2 hidden h-px w-8 -translate-y-1/2 bg-[#302026] lg:block" />
                  ) : null}
                  <div className="flex h-16 w-16 items-center justify-center rounded-[1.35rem] bg-[#2a0d16] text-[#b3314d]">
                    <Icon className="h-7 w-7" />
                  </div>
                  <p className="mt-10 text-sm font-semibold uppercase tracking-[0.18em] text-[#b3314d]">
                    {uiCopy.stepLabel} {index + 1}
                  </p>
                  <h3
                    className={`text-[#fff8f3] ${MARKETING_CARD_TITLE_CLASS}`}
                  >
                    {localizedPageContent.howItWorks[index].title}
                  </h3>
                  <p className={`text-[#968b91] ${MARKETING_CARD_BODY_CLASS}`}>
                    {localizedPageContent.howItWorks[index].text}
                  </p>
                </article>
              ))}
            </div>
          </section>

          <section className="mt-24 grid gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:items-start">
            <div className="max-w-2xl">
              <p className="text-[13px] uppercase tracking-[0.24em] text-[#b3314d]">
                {localizedPageContent.deliverablesEyebrow}
              </p>
              <h2
                className={`font-heading text-[#fff8f3] ${MARKETING_SECTION_TITLE_CLASS}`}
              >
                {localizedPageContent.deliverablesTitle}
              </h2>
              <p
                className={`mt-8 text-[#92878d] ${MARKETING_BODY_READING_CLASS}`}
              >
                {localizedPageContent.deliverablesBody}
              </p>
              <button
                type="button"
                onClick={() => handleDirectCheckout("deliverables_section_cta")}
                disabled={isLaunchingCheckout}
                className={`mt-10 inline-flex items-center justify-center gap-3 rounded-[1rem] bg-[#b3314d] px-7 py-4 sm:py-5 text-white transition hover:bg-[#c63a59] disabled:cursor-not-allowed disabled:opacity-50 ${MARKETING_CTA_TEXT_CLASS}`}
              >
                {localizedPageContent.deliverablesCta}
                <ArrowRight className="h-6 w-6" />
              </button>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              {DELIVERABLES.map(({ icon: Icon }, index) => (
                <article
                  key={localizedPageContent.deliverables[index].title}
                  className="rounded-[2rem] border border-[#2f2026] bg-[#0f090c] p-6 shadow-[0_16px_48px_rgba(0,0,0,0.18)] sm:p-8"
                >
                  <div className="flex h-16 w-16 items-center justify-center rounded-[1.35rem] bg-[#2a0d16] text-[#b3314d]">
                    <Icon className="h-7 w-7" />
                  </div>
                  <h3
                    className={`text-[#fff8f3] ${MARKETING_CARD_TITLE_CLASS}`}
                  >
                    {localizedPageContent.deliverables[index].title}
                  </h3>
                  <p className={`text-[#968b91] ${MARKETING_CARD_BODY_CLASS}`}>
                    {localizedPageContent.deliverables[index].text}
                  </p>
                </article>
              ))}
            </div>
          </section>

          <section className="mt-24">
            <div className="mx-auto max-w-5xl text-center">
              <p className="text-[13px] uppercase tracking-[0.24em] text-[#b3314d]">
                {localizedPageContent.whyEyebrow}
              </p>
              <h2
                className={`font-heading text-[#fff8f3] ${MARKETING_SECTION_TITLE_CLASS}`}
              >
                {localizedPageContent.whyTitle}
              </h2>
              <p
                className={`mt-6 text-[#92878d] ${MARKETING_BODY_READING_CLASS}`}
              >
                {localizedPageContent.whyBody}
              </p>
            </div>

            <div className="mt-14 grid gap-5 lg:grid-cols-4">
              {localizedPageContent.moments.map((occasion) => (
                <article
                  key={occasion.title}
                  className="rounded-[2rem] border border-[#2f2026] bg-[#0f090c] p-6 shadow-[0_16px_48px_rgba(0,0,0,0.18)] sm:p-8"
                >
                  <div className="flex h-16 w-16 items-center justify-center rounded-[1.35rem] bg-[#2a0d16] text-[#b3314d]">
                    <Gift className="h-7 w-7" />
                  </div>
                  <h3
                    className={`text-[#fff8f3] ${MARKETING_CARD_TITLE_CLASS}`}
                  >
                    {occasion.title}
                  </h3>
                  <p className={`text-[#968b91] ${MARKETING_CARD_BODY_CLASS}`}>
                    {occasion.text}
                  </p>
                </article>
              ))}
            </div>

            <div className="mx-auto mt-6 grid max-w-4xl gap-5 lg:grid-cols-2">
              <article className="rounded-[2rem] border border-[#2f2026] bg-[#0f090c] p-6 sm:p-8">
                <div className="flex h-16 w-16 items-center justify-center rounded-[1.35rem] bg-[#2a0d16] text-[#b3314d]">
                  <MapPin className="h-7 w-7" />
                </div>
                <h3 className={`text-[#fff8f3] ${MARKETING_CARD_TITLE_CLASS}`}>
                  {localizedPageContent.citySpecificTitle}
                </h3>
                <p className={`text-[#968b91] ${MARKETING_CARD_BODY_CLASS}`}>
                  {localizedPageContent.citySpecificBody}
                </p>
              </article>
              <article className="rounded-[2rem] border border-[#2f2026] bg-[#0f090c] p-6 sm:p-8">
                <div className="flex h-16 w-16 items-center justify-center rounded-[1.35rem] bg-[#2a0d16] text-[#b3314d]">
                  <Users className="h-7 w-7" />
                </div>
                <h3 className={`text-[#fff8f3] ${MARKETING_CARD_TITLE_CLASS}`}>
                  {localizedPageContent.relationshipTitle}
                </h3>
                <p className={`text-[#968b91] ${MARKETING_CARD_BODY_CLASS}`}>
                  {localizedPageContent.relationshipBody}
                </p>
              </article>
            </div>
          </section>

          <section className="mt-24">
            <div className="mx-auto max-w-5xl text-center">
              <p className="text-[13px] uppercase tracking-[0.24em] text-[#b3314d]">
                {localizedPageContent.detectivesEyebrow}
              </p>
              <h2
                className={`font-heading text-[#fff8f3] ${MARKETING_SECTION_TITLE_CLASS}`}
              >
                {localizedPageContent.detectivesTitle}
              </h2>
            </div>

            <div className="mt-14 grid gap-5 lg:grid-cols-3">
              {localizedPageContent.testimonials.map((testimonial) => (
                <article
                  key={`${testimonial.name}-${testimonial.location}`}
                  className="rounded-[2rem] border border-[#2f2026] bg-[#0f090c] p-6 shadow-[0_16px_48px_rgba(0,0,0,0.18)] sm:p-8"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-[#b3314d]">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <Star key={index} className="h-5 w-5 fill-current" />
                      ))}
                    </div>
                    <span className="text-5xl leading-none text-[#521728]">
                      ”
                    </span>
                  </div>
                  <p
                    className={`mt-8 text-[#e1d9dd] ${MARKETING_BODY_READING_CLASS}`}
                  >
                    "{testimonial.quote}"
                  </p>
                  <div className="mt-10 border-t border-[#2c1d23] pt-7">
                    <p className="text-xl font-semibold text-[#fff8f3] sm:text-2xl">
                      {testimonial.name}
                    </p>
                    <p className="mt-2 text-base text-[#968b91] sm:text-lg">
                      {testimonial.location}
                    </p>
                    <span className="mt-4 inline-flex rounded-lg bg-[#2a0d16] px-4 py-2 text-sm font-medium text-[#d25b77]">
                      {testimonial.tag}
                    </span>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-8 grid gap-5 rounded-[2rem] border border-[#2f2026] bg-[#0d080b] p-6 sm:grid-cols-2 sm:p-8 xl:grid-cols-4">
              {summaryStats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="font-heading text-[2.8rem] font-semibold text-[#b3314d] sm:text-5xl">
                    {stat.value}
                  </p>
                  <p className="mt-4 text-lg text-[#8f848a] sm:text-xl">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-24">
            <div className="mx-auto max-w-5xl text-center">
              <p className="text-[13px] uppercase tracking-[0.24em] text-[#b3314d]">
                {localizedPageContent.faqEyebrow}
              </p>
              <h2
                className={`font-heading text-[#fff8f3] ${MARKETING_SECTION_TITLE_CLASS}`}
              >
                {localizedPageContent.faqTitle}
              </h2>
              <p
                className={`mt-6 text-[#92878d] ${MARKETING_BODY_READING_CLASS}`}
              >
                {localizedPageContent.faqBody}
              </p>
            </div>

            <div className="mx-auto mt-14 max-w-6xl space-y-5">
              {localizedPageContent.faqs.map((item, index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <article
                    key={item.question}
                    className="rounded-[1.8rem] border border-[#2f2026] bg-[#0f090c] px-6 py-6 shadow-[0_12px_36px_rgba(0,0,0,0.14)]"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setOpenFaqIndex((current) =>
                          current === index ? null : index,
                        )
                      }
                      className="flex w-full items-center justify-between gap-4 text-left"
                    >
                      <span className="text-[1.25rem] font-medium leading-tight text-[#fff8f3] sm:text-2xl">
                        {item.question}
                      </span>
                      <ChevronDown
                        className={`h-7 w-7 flex-shrink-0 text-[#9d9098] transition ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {isOpen ? (
                      <p
                        className={`mt-5 max-w-4xl text-[#968b91] ${MARKETING_CARD_BODY_CLASS}`}
                      >
                        {item.answer}
                      </p>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </section>

          <section className="mt-24 rounded-[2rem] border border-[#2f2026] bg-[#0f090c] p-6 shadow-[0_20px_60px_rgba(0,0,0,0.24)] sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="max-w-3xl">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#b3314d] sm:text-[11px]">
                  {entryCopy.plannerEyebrow}
                </p>
                <h2
                  className={`mt-3 font-heading text-[#fff8f3] ${MARKETING_CARD_TITLE_CLASS}`}
                >
                  {entryCopy.plannerTitle}
                </h2>
                <p
                  className={`mt-3 text-[#92878d] ${MARKETING_CARD_BODY_CLASS}`}
                >
                  {entryCopy.plannerBody}
                </p>
              </div>
              <button
                type="button"
                onClick={handlePlannerToggle}
                className="rounded-full border border-[#b3314d]/35 bg-[#2a0d16] px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-[#f1e0c1] transition hover:bg-[#3a101d]"
              >
                {showPlanner ? entryCopy.hidePlanner : entryCopy.openPlanner}
              </button>
            </div>

            {showPlanner ? (
              <div className="mt-6 rounded-[1.5rem] border border-[#2f2026] bg-[#0d080b] p-5 sm:rounded-[2rem]">
                <CustomCaseFormFields
                  formData={formData}
                  errors={localizedErrors}
                  copy={formCopy}
                  disabled={isLaunchingCheckout}
                  submitLabel={uiCopy.plannerSubmitLabel}
                  onSubmit={(event) => {
                    void handlePlannerSubmit(event);
                  }}
                  onFormDataChange={applyFormDataUpdate}
                  onAddSuspect={handleAddSuspect}
                  onRemoveSuspect={handleRemoveSuspect}
                  onSuspectChange={handleSuspectChange}
                />
              </div>
            ) : (
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                <div className="rounded-[1.5rem] border border-[#2f2026] bg-[#0d080b] p-4 sm:rounded-[1.8rem]">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-[#b3314d]">
                    {localizedPageContent.plannerStatusLabel}
                  </p>
                  <p className="mt-2 text-sm text-[#fff7ef]">
                    {hasPlannerData
                      ? entryCopy.draftReady
                      : entryCopy.draftEmpty}
                  </p>
                </div>
                <div className="rounded-[1.5rem] border border-[#2f2026] bg-[#0d080b] p-4 sm:rounded-[1.8rem]">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-[#b3314d]">
                    {localizedPageContent.suspectsLabel}
                  </p>
                  <p className="mt-2 text-sm text-[#fff7ef]">
                    {formData.suspects.length > 0
                      ? `${formData.suspects.length}`
                      : localizedPageContent.addUpTo}
                  </p>
                </div>
                <div className="rounded-[1.5rem] border border-[#2f2026] bg-[#0d080b] p-4 sm:rounded-[1.8rem]">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-[#b3314d]">
                    {localizedPageContent.cityLabel}
                  </p>
                  <p className="mt-2 text-sm text-[#fff7ef]">
                    {formData.city || entryCopy.cityEmpty}
                  </p>
                </div>
              </div>
            )}
          </section>

          <section className="mt-24 pb-6 text-center">
            <p className="text-[13px] uppercase tracking-[0.24em] text-[#b3314d]">
              {entryCopy.finalEyebrow}
            </p>
            <h2
              className={`mx-auto mt-5 max-w-5xl font-heading text-[#fff8f3] ${MARKETING_SECTION_TITLE_CLASS}`}
            >
              {entryCopy.finalTitle}
              <br />
              <span className="text-[#9f9aa0]">{entryCopy.finalSubtitle}</span>
            </h2>
            <p
              className={`mx-auto mt-8 max-w-5xl text-[#92878d] ${MARKETING_BODY_READING_CLASS}`}
            >
              {entryCopy.finalBody}
            </p>
            <button
              type="button"
              onClick={() => handleDirectCheckout("final_section_cta")}
              disabled={isLaunchingCheckout}
              className={`mt-10 inline-flex items-center justify-center gap-3 rounded-[1rem] bg-[#b3314d] px-8 py-4 sm:py-5 text-white shadow-[0_22px_70px_rgba(179,49,77,0.25)] transition hover:bg-[#c63a59] disabled:cursor-not-allowed disabled:opacity-50 ${MARKETING_CTA_TEXT_CLASS}`}
            >
              {isLaunchingCheckout
                ? entryCopy.preparingButton
                : `${entryCopy.finalButton} — ${customCasePriceDisplay.inline}`}
              <ArrowRight className="h-6 w-6" />
            </button>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-8 text-lg text-[#968b91]">
              <div className="inline-flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 text-[#b3314d]" />
                {localizedPageContent.secureViaFastSpring}
              </div>
              <div className="inline-flex items-center gap-3">
                <Clock3 className="h-5 w-5 text-[#b3314d]" />
                {localizedPageContent.instantDelivery}
              </div>
            </div>
            <div className="mx-auto mt-10 inline-flex rounded-full border border-[#3a1f27] bg-[#13090d] px-6 py-3 text-xl text-[#e7e0e3] shadow-[0_20px_50px_rgba(0,0,0,0.2)] sm:px-8 sm:py-4 sm:text-2xl">
              <span className="font-semibold text-[#b3314d]">
                {activeBuilderCount}
              </span>
              <span className="ml-2">
                {localizedPageContent.buildersSuffix}
              </span>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default CustomStoriesPage;
